import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Select,
  Table,
  Input,
  Form,
  Spin,
  Button,
  Modal,
  Checkbox,
  DatePicker,
  Upload,
  notification,
} from "antd";
import styled, { css } from "styled-components";
import moment from "moment";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { findWeekId, getWeekStartDate } from "utils/Date";
import { useDispatch, useSelector } from "react-redux";
import {
  getClientid,
  getUserInfo,
  getUserList,
  getUserRoleId,
} from "redux/selectors";
import { useQueryState } from "hooks/useQueryState";
import useMultipleFetch from "hooks/useMultipleFetch";
import { sumBy } from "lodash";
import { ReactComponent as ClearIcon } from "assets/clear.svg";
import withdrawIcon from "assets/timesheeticons/withdraw.png";
import notifyIcon from "assets/timesheeticons/notify.png";
import editIcon from "assets/timesheeticons/edit.png";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  CheckSquareFilled,
  CloseSquareFilled,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  DRAFT,
  MAX_WORKING_HOURS,
  MIN_WORKING_HOURS,
  TOTAL_WORKING_HOURS,
  WITHDRAWN,
} from "pages/TimeSheet/constants";
import { TimeSheetStatus } from "pages/TimeSheet/TimeSheetStatus";
import ApproveActionButton from "pages/TimeSheet/ApproveActionButton";
import COLORS from "common/Colors";
import { initialClaim } from "pages/TimeSheet/InitialTimeSheetData";
import { UploadOutlined } from "@mui/icons-material";
import { useMutate } from "hooks/useMutate";
import { allowOnlyNumbers, fileToBase64 } from "utils/functions";
import BillingResourceSummary from "pages/TimeSheet/BillingResourceSummary";
import { uniqBy } from "lodash";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import useTimesheetfns from "pages/TimeSheet/useTimesheetfns";
import { setActualSummaryData } from "redux/actions";
import { useDelete } from "hooks/useDelete";

const StyledButton = styled(Button)`
  background: #00685F !important;
  border-radius: 5px;
  color: #ffffff !important;
  width: 100px;
   &:hover {
      color: #ffffff !important;
  }
   &:focus {
      color: #ffffff !important;
  }
`;

const StyledButtonCancel = styled(Button)`
  background: #ffffff !important;
  border-radius: 5px;
  color: #000000!important;
  width: 100px;
  border-color: #000000!important;
  &:focus {
      color: #000000 !important;
  }
`;

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
let actualChangingValue = 0;
let actualChangingData = [];

const normFile = (e) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

const DayRender = (day, date, month) => {
  return (
    <div>
      <div className="timesheet-day">{day}</div>
      <div className="timesheet-date">
        {date} {month}
      </div>
    </div>
  );
};

const TimeSheet = ({
  editable,
  data,
  approval = false,
  extraActions,
  employeeId = false,
  timesheetgetdata,
  selectedTabId,
  week,
  weekNavNode,
  listMetaNode,
  editables = false,
  showClaims = true,
}) => {
  const monday = moment(getWeekStartDate(data?.[0]?.currentWeek), "YYYY-MM-DD");
  const sunday = moment(
    getWeekStartDate(data?.[0]?.currentWeek),
    "YYYY-MM-DD"
  ).add(6, "days");
  const weekId = findWeekId(monday, sunday);
  const userInfo = useSelector(getUserInfo);
  const userList = useSelector(getUserList);
  const clientId = useSelector(getClientid);
  const userRoleId = useSelector(getUserRoleId);

  const currentUser = userList.find((user) => user.userid === userInfo.userid);

  const { key, url } = queryKeys.getResourePlanningList(
    findWeekId(monday, sunday)
  );

  const { data: resourcesData } = useFetch(
    key,
    url,
    {
      enabled: true,
    },
    {
      startDate: new Date(monday),
      endDate: new Date(sunday),
      employeeIds: employeeId ? [employeeId] : [userInfo?.userid],
      engagementIds: [],
      weekId: [weekId],
      managingOffice: currentUser?.managingoffice?.[0],
    }
  );

  const resources = resourcesData?.response?.response;
  const uniqueEngagmentIDs = [
    ...new Set(resources?.map((resource) => resource.matterId)),
  ];

  const engagementResult = useMultipleFetch(
    uniqueEngagmentIDs?.length > 0
      ? uniqueEngagmentIDs?.map((matterId) => {
        const { key, url } = queryKeys.getMatterById(matterId);

        return {
          queryKey: key,
          url,
        };
      })
      : []
  );

  const unqiueClientIDs = [
    ...new Set(
      engagementResult
        .filter((item) =>
          item?.status === "success" &&
          item?.data?.response &&
          item?.data?.response?.data?.financial_resourcePlan?.pfaStatus !==
          "Not Approved" &&
          item?.data?.response?.data?.matterStatus !== "Closed" &&
          item?.data?.response?.data?.matterStatus !== "Draft"
        )
        .map((item) => item?.data?.response?.data?.clientId)
    ),
  ];

  const results = useMultipleFetch(
    unqiueClientIDs?.length > 0
      ? unqiueClientIDs?.map((clientId) => {
        const { key, url } = queryKeys.getClient(clientId);

        return {
          queryKey: key,
          url,
        };
      })
      : []
  );

  const clientOptions = results?.map((result) => {
    return {
      label: result?.data?.response?.data?.clienttype === "company" ? result?.data?.response?.data?.companyregisteredname : result?.data?.response?.data?.clientname,
      value: result?.data?.response?.data?.bdo_id,
      isLoaded: result?.isFetched,
    };
  });

  const engagements = resources?.map((resource) => {
    return {
      clientId: resource.clientId,
      matterId: resource.matterId,
      name: resource.matterName,
      resourceId: resource.resourceId,
    };
  });

  const containerRef = useRef(null);
  const [editingIndex, setEditingIndex] = useState(null);

  const { key: holidayKey, url: holdiayUrl } =
    queryKeys.getHolidayList(clientId);

  const { key: leaveKey, url: leaveUrl } = queryKeys.getLeaveList(
    clientId,
    userInfo?.userid
  );

  const { data: holidayDataRes } = useFetch(
    [holidayKey, monday, sunday], // Make the key reactive
    holdiayUrl,
    {
      enabled: !!clientId,
    },
    {
      start_date: monday,
      end_date: sunday,
    }
  );

  const leaveParams = {
    status_id: "",
    officesupervisorid: [""],
    selected_role_id: userRoleId,
    start_date: monday,
    end_date: sunday,
  };

  const { data: leaveDataRes } = useFetch(
    [leaveKey, monday, sunday],
    leaveUrl,
    {
      enabled: !!clientId,
    },
    leaveParams
  );

  const leaves =
    leaveDataRes?.response?.leaves?.filter(
      (leave) =>
        leave.userid === userInfo.userid &&
        leave?.status_id === "5"
      // leave?.having_cancel_request === "0"
    ) || [];
  const holidays = holidayDataRes?.response?.filter((holiday) => {
    return holiday?.location_id?.includes(currentUser?.baselocationid);
  });

  const {
    columns,
    dataSource,
    selectedTimesheetKeys,
    setSelectedTimesheetKeys,
    eligibleTimesheetKeys,
    weekDays,
    allTotal,
  } = useTimeSheetDetails(
    data,
    editable,
    approval,
    clientOptions,
    engagements,
    extraActions,
    containerRef,
    holidays,
    leaves,
    timesheetgetdata,
    selectedTabId,
    resources,
    editables,
    week,
    editingIndex
  );

  const typeColumn = columns.find((col) => col.key === "type");
  const editingRow = dataSource.find((row) => row.key === editingIndex);

  const { handleWithdraw, handleResubmitDeclined, isResubmitting } =
    extraActions || {};

  return (
    <TimeSheetWrap ref={containerRef}>
      <WeekTotalsBar>
        {weekNavNode && (
          <div className="week-nav-cell">
            {weekNavNode}
            {approval && (
              <Checkbox
                className="approve-select-all-checkbox"
                disabled={eligibleTimesheetKeys.length === 0}
                checked={
                  eligibleTimesheetKeys.length > 0 &&
                  eligibleTimesheetKeys.every((key) =>
                    selectedTimesheetKeys.includes(key)
                  )
                }
                onChange={(e) =>
                  setSelectedTimesheetKeys(
                    e.target.checked ? eligibleTimesheetKeys : []
                  )
                }
              >
                <span style={{ color: "#1f243b" }}>Select All</span>
              </Checkbox>
            )}
          </div>
        )}
        <div className="week-nav-spacer" />
        <div className="days-row">
          {weekDays.map((day) => (
            <div className="day-cell" key={day.key}>
              <div className="day-date-num">{day.date.format("D")}</div>
              <div className="day-name">{day.date.format("ddd")}</div>
              <TotalRender
                total={day.total}
                min={MIN_WORKING_HOURS}
                max={MAX_WORKING_HOURS}
                isSatOrSun={day.key === "sat" || day.key === "sun"}
              />
            </div>
          ))}
          <div className="day-cell total-cell">
            <div className="day-date-num">Total</div>
            <div className="day-hour">Hours</div>
            <TotalRender
              total={allTotal}
              min={MIN_WORKING_HOURS * 5}
              max={MAX_WORKING_HOURS * 5}
            />
          </div>
        </div>
      </WeekTotalsBar>

      {listMetaNode}

      <EntryCardsWrap className="timeSheetCards">
        {dataSource.map((row) => {
          const isBeingEdited =
            row.rowStatus === "declined" && editingIndex === row.key;

          if (!row.isEditable) {
            return (
              <EntryCard key={row.key} className="locked">
                {approval && (
                  <div className="locked-checkbox">
                    {typeColumn?.render(row.type, row)}
                  </div>
                )}

                <div className="locked-body">
                  <div className="locked-header">
                    <span className="entry-client-name">{row.clientLabel}</span>
                    <span className="entry-matter-name">{row.matterLabel}</span>
                    {row.type}

                    <div className="header-actions">
                      {row.rowStatus === "declined" && !approval && (
                        <img
                          src={editIcon}
                          alt="Editable"
                          className="status-icon clickable"
                          title="Editable"
                          onClick={() => setEditingIndex(row.key)}
                        />
                      )}
                      {row.rowStatus === "pending" && !approval && (
                        <>
                          <img
                            src={withdrawIcon}
                            alt="Withdraw Submission"
                            className="status-icon clickable"
                            title="Withdraw Submission"
                            onClick={() => handleWithdraw?.(row.key)}
                          />
                          {/* <img
                            src={notifyIcon}
                            alt="Notify"
                            className="status-icon"
                            title="Notify"
                          /> */}
                        </>
                      )}
                      {row.action}
                    </div>
                  </div>

                  <div className="locked-content">
                    <div className="locked-fields">
                      <div className="locked-field">
                        <label>Task Type</label>
                        <span>{row.taskLabel}</span>
                      </div>

                      {row.remarksText && (
                        <div className="locked-field">
                          <label>Remarks</label>
                          <span>{row.remarksText}</span>
                        </div>
                      )}
                      {row.rowStatus === "declined" && row.declinedReasonText && (
                        <div className="locked-field">
                          <label>Reason for Decline</label>
                          <span>{row.declinedReasonText}</span>
                        </div>
                      )}
                    </div>

                    <div className="hours-strip">
                      {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map(
                        (d) => (
                          <div className="hour-box plain" key={d}>
                            {row[d]}
                          </div>
                        )
                      )}
                      <div className="total-box locked-total">{row.sum}</div>
                    </div>
                  </div>
                </div>
              </EntryCard>
            );
          }

          return (
            <EntryCard key={row.key} className="editable">
              <div className="card-row card-row-1">
                <div className="field client-field">
                  <label>Client</label>
                  {!isBeingEdited && row.client}
                </div>
                {!isBeingEdited && (
                  <MatterFieldRender
                    index={row.key}
                    engagementNode={row.engagement}
                    ncClientNode={row.ncClient}
                  />
                )}

                <div className="hours-strip">
                  {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map(
                    (d) => (
                      <div className="hour-box" key={d}>
                        {!isBeingEdited && row[d]}
                      </div>
                    )
                  )}
                  <div className="total-box">{!isBeingEdited && row.sum}</div>
                </div>
              </div>

              <div className="card-row card-row-2">
                {!isBeingEdited && row.extraSection}
                <div className="row-footer">
                  {!isBeingEdited && (
                    <ActualToBudgetGroup
                      index={row.key}
                      budgetNode={row.budget}
                      actualNode={row.actual_to_date}
                    />
                  )}
                  <div className="row-footer-actions">
                    {!isBeingEdited && showClaims && row.claims}
                    {!isBeingEdited && row.action}
                  </div>
                </div>
              </div>
            </EntryCard>
          );
        })}
      </EntryCardsWrap>

      <ApproveActionButton
        selectedTimesheetKeys={selectedTimesheetKeys}
        setSelectedTimesheetKeys={setSelectedTimesheetKeys}
        eligibleTimesheetKeys={eligibleTimesheetKeys}
      />

      <ResubmitModal
        open={!!editingRow}
        onCancel={() => setEditingIndex(null)}
        title="Editing Timesheet"
        footer={null}
        closable={true}
        maskClosable={false}
        width={640}
        destroyOnClose={true}
      >
        {editingRow && (
          <>
            <div className="resubmit-card">
              <div className="resubmit-fields-row">
                <div className="field client-field">
                  <label>Client</label>
                  {editingRow.client}
                </div>
                <MatterFieldRender
                  index={editingRow.key}
                  engagementNode={editingRow.engagement}
                  ncClientNode={editingRow.ncClient}
                />
              </div>

              <div className="resubmit-fields-row">{editingRow.extraSection}</div>

              <div className="resubmit-days-row">
                {weekDays.map((day) => (
                  <div className="resubmit-day-cell" key={day.key}>
                    <div className="resubmit-day-date">{day.date.format("D")}</div>
                    <div className="resubmit-day-name">{day.date.format("ddd")}</div>
                    <TotalRender
                      total={day.total}
                      min={MIN_WORKING_HOURS}
                      max={MAX_WORKING_HOURS}
                      isSatOrSun={day.key === "sat" || day.key === "sun"}
                    />
                  </div>
                ))}
                <div className="resubmit-day-cell total-cell">
                  <div className="resubmit-day-date">Total</div>
                  <div className="resubmit-day-name">Hours</div>
                  <TotalRender
                    total={allTotal}
                    min={MIN_WORKING_HOURS * 5}
                    max={MAX_WORKING_HOURS * 5}
                  />
                </div>
              </div>

              <div className="resubmit-hours-row">
                {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((d) => (
                  <div className="hour-box" key={d}>
                    {editingRow[d]}
                  </div>
                ))}
                <div className="total-box">{editingRow.sum}</div>
              </div>
            </div>

            <div className="resubmit-actions">
              <StyledButtonCancel onClick={() => setEditingIndex(null)}>Cancel</StyledButtonCancel>
              <StyledButton
                // type="primary"
                className="resubmit-submit"
                loading={isResubmitting}
                onClick={() =>
                  handleResubmitDeclined?.(editingIndex, () => setEditingIndex(null))
                }
              >
                Submit
              </StyledButton>
            </div>
          </>
        )}
      </ResubmitModal>
    </TimeSheetWrap>
  );
};

export default TimeSheet;

const MatterFieldRender = ({ index, engagementNode, ncClientNode }) => {
  const form = Form.useFormInstance();
  const client = Form.useWatch([index, "client"], form);

  if (client === "nc") {
    return (
      <div className="field matter-field">
        <label>NC Client</label>
        {ncClientNode}
      </div>
    );
  }

  return (
    <div className="field matter-field">
      <label>Job / Matter</label>
      {engagementNode}
    </div>
  );
};

const ActualToBudgetGroup = ({ index, budgetNode, actualNode }) => {
  const form = Form.useFormInstance();
  const client = Form.useWatch([index, "client"], form);
  const matterId = Form.useWatch([index, "engagement"], form);

  if (!matterId || client === "nc" || client === "ap") {
    return null;
  }

  return (
    <span className="actual-to-budget">
      Actual to Budget (Hrs): {actualNode}/{budgetNode}
    </span>
  );
};

const ClientRender = ({
  editable,
  name,
  clientOptions,
  extraActions,
  containerRef,
  status,
}) => {
  const isSaturdayORSunday = name[1] === "sat" || name[1] === "sun";
  const form = Form.useFormInstance();
  const { handleRemoveClaim } = extraActions || {};
  const clientName = Form.useWatch([name[0], "client"], form);

  const selectedClient = clientName
    ? clientOptions?.find((client) => client?.value === clientName)
    : null;

  // const isLoaded = selectedClient
  //   ? selectedClient?.isLoaded
  //   : editable
  //     ? true
  //     : false;
  const isLoaded = selectedClient
    ? selectedClient?.isLoaded
    : editable || status === "draft";

  useEffect(() => {
    if (clientName === "nc") {
      form.setFieldsValue({
        [name[0]]: {
          engagement: "nc",
        },
      });
    }

    if (clientName === "ap") {
      form.setFieldsValue({
        [name[0]]: {
          engagement: "ap",
        },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientName]);

  return (
    <>
      {/* {!editable ? (
        <>
          <span className="client-name" onClick={handleShowClientDetails}>
            {clientName}
          </span>

          <BillingResourceSummary
            show={showModal}
            onClose={handleCloseModal}
            engagementData={engagementData}
            clientName={clientName}
          />
        </>
      ) : ( */}
      <Spin size="small" spinning={!isLoaded}>
        <Form.Item
          name={name}
          rules={[
            {
              required: !isSaturdayORSunday,
              message: "Required",
            },
          ]}
        >
          <Select
            showSearch
            placeholder="Select"
            allowClear={true}
            style={{ flex: 1, width: "100%" }}
            getPopupContainer={() => containerRef.current}
            optionFilterProp="label"
            options={clientOptions
              ?.filter((client) => client?.value !== "ap")
              ?.map((client) => {
                return {
                  label: client.isLoaded ? (
                    client.label
                  ) : (
                    <>
                      <Spin /> Loading{" "}
                    </>
                  ),
                  value: client.value,
                };
              })}
            value={
              selectedClient?.value === "ap"
                ? "AP"
                : selectedClient?.value || null
            }
            onChange={(value) => {
              if (value === "nc" || value === "ap") {
                if (handleRemoveClaim) {
                  handleRemoveClaim(name[0]);
                }
              }
              form.setFieldValue([name[0], "engagement"], null);
            }}
            disabled={!editable || clientName === "ap"} // Disable if client is "ap"
          />
        </Form.Item>
      </Spin>
      {/* )} */}
    </>
  );
};

const EngagementRender = ({ editable, name, engagements, containerRef }) => {
  const isSaturdayORSunday = name[1] === "sat" || name[1] === "sun";

  const form = Form.useFormInstance();
  const client = Form.useWatch([name[0], "client"], form);

  const matterId = Form.useWatch([name[0], "engagement"], form);

  const isClientSelected = !!client;

  let engagementOptions = engagements
    ?.filter((engagement) => {
      return engagement.clientId === client;
    })
    ?.map((engagement) => ({
      label: engagement.name,
      value: engagement.matterId,
    }));
  if (client === "nc") {
    engagementOptions = [
      {
        label: "Non-Chargeable",
        value: "nc",
      },
    ];
  }

  if (client === "ap") {
    engagementOptions = [
      {
        label: "Approved Absence",
        value: "ap",
      },
    ];
  }

  engagementOptions = uniqBy(engagementOptions, "value");

  const selectedEngagement = matterId
    ? engagementOptions?.find(
      (engagement) => engagement?.value === matterId
    )
    : null;

  useEffect(() => {
    if (matterId) {
      const selected = engagements?.find(
        (e) => e?.matterId === matterId
      );
      if (selected?.resourceId) {
        form.setFieldsValue({
          [name[0]]: {
            resourceId: selected.resourceId,
          },
        });
      }
    }
  }, [matterId]);

  return (
    <Form.Item
      name={name}
      rules={[
        {
          required: !isSaturdayORSunday,
          message: "Required",
        },
      ]}
    >
      <Select
        placeholder="Select"
        allowClear={true}
        style={{ flex: 1, width: "100%" }}
        options={engagementOptions}
        value={selectedEngagement?.value || undefined}
        disabled={!editable || !isClientSelected || client === "ap"} // Disable if client is "ap"
        getPopupContainer={() => containerRef.current}
      />
    </Form.Item>
  );
};

const TaskRender = ({
  editable,
  name,
  containerRef,
  holidays,
  leaves,
  data,
  timesheetgetdata,
}) => {
  const form = Form.useFormInstance();
  const userInfo = useSelector(getUserInfo);

  const NCTaskOptions = [
    {
      label: "Accounting, Invoicing & Debtors",
      value: "Accounting, Invoicing & Debtors",
    },
    {
      label: "Administration Services",
      value: "Administration Services",
    },
    // {
    //   label: "Administrative",
    //   value: "Administrative",
    // },
    {
      label: "Benched Time",
      value: "Benched Time",
    },
    {
      label: "Business Development",
      value: "Business Development",
    },
    {
      label: "Business Travel",
      value: "Business Travel",
    },
    {
      label: "HR Activities & Events",
      value: "HR Activities & Events",
    },
    {
      label: "HR Operations",
      value: "HR Operations",
    },
    {
      label: "Independence and COI Checks",
      value: "Independence and COI Checks",
    },
    // {
    //   label: "Internal Sharing",
    //   value: "Internal Sharing",
    // },
    {
      label: "Local & Regional Reporting",
      value: "Local & Regional Reporting",
    },
    {
      label: "Meeting",
      value: "Meeting",
    },
    {
      label: "Office Non Chargeable",
      value: "Office Non Chargeable",
    },
    {
      label: "Others - Explain in Remark",
      value: "Others - Explain in Remark",
    },
    {
      label: "Proposal Writing",
      value: "Proposal Writing",
    },
    {
      label: "Timesheets & Claims Reviews",
      value: "Timesheets & Claims Reviews",
    },
    {
      label: "Training External",
      value: "Training External",
    },
    {
      label: "Training internal",
      value: "Training internal",
    },
  ];

  const COptions = [
    {
      label: "Planning",
      value: "Planning",
    },
    {
      label: "Execution",
      value: "Execution",
    },
    {
      label: "Reporting",
      value: "Reporting",
    },
    {
      label: "Follow up & Closure",
      value: "Follow up & Closure",
    },
    // {
    //   label: "Public Holiday",
    //   value: "Public Holiday",
    // },
    // {
    //   label: "Leave",
    //   value: "Leave",
    // },
  ];
  const formValues = Form.useWatch([], form) || {};
  const client = Form.useWatch([name[0], "client"], form);
  const isHoliday = Form.useWatch([name[0], "isHoliday"], form);
  const isLeave = Form.useWatch([name[0], "isLeave"], form);
  const isInBetweenHolidays = () => {
    const currentWeekStartDate = moment(
      getWeekStartDate(data?.[0]?.currentWeek),
      "YYYY-MM-DD"
    ); // Correct as string
    const weekStart = moment(currentWeekStartDate, "YYYY-MM-DD");
    // Check all 7 days of the week
    for (let i = 0; i < 7; i++) {
      const currentDate = weekStart
        .clone()
        .add(i, "days")
        .startOf("day")
        .toDate();

      const isHoliday = holidays?.some((holiday) => {
        const startDate = moment(holiday.start_date, "YYYY-MM-DD")
          .startOf("day")
          .toDate();
        const endDate = moment(holiday.end_date, "YYYY-MM-DD")
          .startOf("day")
          .toDate();

        return currentDate >= startDate && currentDate <= endDate;
      });

      if (isHoliday) {
        return true; // If any day matches a holiday, return true
      }
    }

    return false; // No holiday found in the week
  };

  const isLeaveInWeek = () => {
    const currentWeekStartDate = moment(
      getWeekStartDate(data?.[0]?.currentWeek),
      "YYYY-MM-DD"
    );
    const weekStart = moment(currentWeekStartDate, "YYYY-MM-DD");
    for (let i = 0; i < 7; i++) {
      const currentDate = weekStart
        .clone()
        .add(i, "days")
        .startOf("day")
        .toDate();
      const filterData = leaves?.filter(
        (data) =>
          data?.userid === (timesheetgetdata?.employeeId || userInfo?.userid)
      );
      const isLeaveDay = filterData?.some((leave) => {
        const startDate = moment(leave.start_date, "YYYY-MM-DD")
          .startOf("day")
          .toDate();
        const endDate = moment(leave.end_date, "YYYY-MM-DD")
          .startOf("day")
          .toDate();
        const matched = currentDate >= startDate && currentDate <= endDate;
        return matched;
      });
      if (isLeaveDay) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    const holidayStatus = isInBetweenHolidays();
    const leaveStatus = isLeaveInWeek();
    const row = formValues?.[name[0]];
    if (row?.client === "ap") {
      if (holidayStatus && leaveStatus) {
        form.setFieldValue([name[0], "task"], "Public Holiday / Leave");
      } else if (holidayStatus) {
        form.setFieldValue([name[0], "task"], "Public Holiday");
      } else if (leaveStatus && !row?.task) {
        form.setFieldValue([name[0], "task"], "Leave");
      }
    }
  }, [formValues, name, form, holidays, leaves, data]);
  return (
    <Form.Item
      name={name}
      rules={[
        {
          required: true,
          message: "Required",
        },
      ]}
    >
      <Select
        placeholder="Select Task"
        style={{ flex: 1, width: "100%" }}
        getPopupContainer={() => containerRef.current}
        allowClear={true}
        options={client === "nc" ? NCTaskOptions : COptions}
        disabled={!editable || client === "ap" || isHoliday || isLeave}
      />
    </Form.Item>
  );
};

const BudgetRender = ({ name, data }) => {
  const form = Form.useFormInstance();

  const matterId = Form.useWatch([name[0], "engagement"], form);
  const budget = Form.useWatch([name[0], "budget"], form);

  const userInfo = useSelector(getUserInfo);

  const { key, url } = queryKeys.getMatterById(matterId);

  const { isFetched } = useQueryState(key);

  const { data: engagementDataRes, isLoading: isEngagmentLoading } = useFetch(
    key,
    url,
    {
      enabled: !isFetched && !!matterId && !budget,
    }
  );

  const engagementData = engagementDataRes?.response?.data;

  useEffect(() => {
    const isViewingTimesheet =
      window.location.pathname.includes("timesheet-detailed");
    if (!isViewingTimesheet && engagementData) {
      const totalEmployees = engagementData?.employeeList ?? [];
      const currentResource = totalEmployees.filter(
        (employee) => employee?.userId === userInfo?.userid
      );
      let totalPersonDays = currentResource.reduce((sum, item) => {
        return sum + (item.personDay ? Number(item.personDay) : 0);
      }, 0);
      form.setFieldsValue({
        [name[0]]: {
          budget: (totalPersonDays || 0) * TOTAL_WORKING_HOURS,
        },
      });
    } else if (isViewingTimesheet && data?.length > 0) {
      const budgetFromApi = data[0]?.budget ?? 0;
      form.setFieldsValue({
        [name[0]]: {
          budget: budgetFromApi,
        },
      });
    }
  }, [form, engagementData, name, userInfo, data]);

  return (
    <Spin spinning={isEngagmentLoading}>
      {/* Form.Item is kept but visually hidden - it preserves the value in
          the antd form model (used elsewhere for calculations/submission)
          while the actual visible display below is a plain <span>. A
          disabled <Input> carries antd Form.Item wrapper markup that
          claims block-level flex space regardless of the input's own
          width/padding, which is what caused the persistent gap before
          the slash in "Actual to Budget (Hrs): 0 / 80". A <span> has no
          such wrapper and sizes exactly to its text. */}
      <Form.Item name={name} className="atb-hidden-field">
        <Input disabled placeholder="-" type="number" />
      </Form.Item>
      <span className="atb-text">{budget ?? "-"}</span>
    </Spin>
  );
};

const ActualToDateRender = ({
  name,
  extraActions,
  engagements,
  timesheetgetdata,
  data,
  selectedTabId,
  week,
}) => {
  const userInfo = useSelector(getUserInfo);
  const form = Form.useFormInstance();
  const dispatch = useDispatch();
  const [actualSummaryState, setActualSummaryState] = useState(null);

  const matterId = Form.useWatch([name[0], "engagement"], form);
  const actualToDate = Form.useWatch([name[0], "actual_to_date"], form);
  const formValues = Form.useWatch([], form) || {};

  const resourceId = engagements?.find(
    (e) => e?.matterId === matterId
  )?.resourceId;

  const userId = timesheetgetdata?.employeeId
    ? timesheetgetdata?.employeeId
    : userInfo?.userid;
  const { key, url } = queryKeys.getMatterById(matterId);
  const { isFetched } = useQueryState(key);
  const { data: engagementDataRes, isLoading: isEngagmentLoading } = useFetch(
    key,
    url,
    {
      enabled: !isFetched && !!matterId && !actualToDate,
    }
  );
  const { key: actualSummaryKey, url: actualSummaryUrl } =
    queryKeys.getActualSummary(matterId, userId);

  const { data: actualSummaryData, refetch } = useFetch(
    actualSummaryKey,
    actualSummaryUrl,
    {
      // enabled: !!matterId && !!resourceId && !!week && !actualToDate,
      onSuccess: (response) => {
        setActualSummaryState(response?.response?.data);
        dispatch(setActualSummaryData(response?.response?.data));
      },
      onError: (error) => {
        console.error("Error fetching actual summary:", error);
        setActualSummaryState(null);
        dispatch(setActualSummaryData(null));
      },
    }
  );

  const engagementData = engagements?.find(
    (e) => e.matterId === matterId
  );
  const engagementDatas = engagementDataRes?.response?.data;
  const { handlePersonInCharge } = extraActions || {};
  useEffect(() => {
    if (engagementDatas) {
      if (handlePersonInCharge)
        handlePersonInCharge(
          name[0],
          engagementDatas?.referringAttorney
        );
    }
  }, [engagementDatas, handlePersonInCharge]);

  // const totalApiActualHours =
  //   actualSummaryState?.actualTimesheet
  //     ?.filter(
  //       (item) =>
  //         item.resourceId === resourceId &&
  //         (item.week <= week || item.week === week)
  //     )
  //     ?.reduce((sum, item) => sum + (parseFloat(item.sum) || 0), 0) || 0;

  const totalApiActualHours =
    actualSummaryState?.actualTimesheet
      ?.filter((item) => {
        if (item.resourceId !== resourceId) return false;

        const itemStartDate = moment(item?.week?.split("-")[0], "YYYYMMMDD");
        const targetStartDate = moment(week?.split("-")[0], "YYYYMMMDD");

        return itemStartDate.isSameOrBefore(targetStartDate);
      })
      ?.reduce((sum, item) => sum + (parseFloat(item.sum) || 0), 0) || 0;

  useEffect(() => {
    const client = Object.entries(formValues)?.filter(
      ([key, value]) => key === String(name[0])
    );
    const filtered = Object.entries(formValues)
      ?.filter(([key, value]) => key !== String(name[0]))
      ?.filter(([key, value]) => value?.client === client?.[0]?.[1]?.client)
      ?.filter(([_, value]) => value?.engagement !== "ap")
      ?.filter(([_, value]) => value?.engagement !== "nc")
      ?.reduce((sum, [_, value]) => sum + (parseFloat(value?.sum) || 0), 0);

    if (!engagementData || !formValues[name[0]]) return;
    const rowData = formValues[name[0]];
    const formActual =
      (parseFloat(rowData.mon) || 0) +
      (parseFloat(rowData.tue) || 0) +
      (parseFloat(rowData.wed) || 0) +
      (parseFloat(rowData.thu) || 0) +
      (parseFloat(rowData.fri) || 0) +
      (parseFloat(rowData.sat) || 0) +
      (parseFloat(rowData.sun) || 0);

    const getStartDate = (weekStr) => {
      const start = weekStr?.split("-")?.[0]; // e.g., "2020FEB03"
      return moment(start, "YYYYMMMDD"); // parse with moment
    };

    const currentWeekStart = getStartDate(week);

    const previousWeeksApiHours =
      actualSummaryState?.actualTimesheet
        ?.filter(
          (item) =>
            item.resourceId === resourceId &&
            getStartDate(item.week).isBefore(currentWeekStart)
          // Exclude "ap" engagements
        )
        ?.reduce((sum, item) => sum + (parseFloat(item.sum) || 0), 0) || 0;
    const currentWeekApiEntry = actualSummaryState?.actualTimesheet?.find(
      (item) => item.week === week && item.resourceId === resourceId
    );

    let calculatedTotal = 0;

    if (currentWeekApiEntry) {
      // calculatedTotal = totalApiActualHours - filtered;
      calculatedTotal = totalApiActualHours;
    } else {
      calculatedTotal = previousWeeksApiHours + formActual;
    }
    const currentVal = rowData.actual_to_date;
    if (calculatedTotal !== currentVal && !isNaN(calculatedTotal)) {
      form.setFieldsValue({
        [name[0]]: {
          ...rowData,
          actual_to_date: calculatedTotal,
        },
      });
    }
  }, [
    formValues[name[0]?.toString()],
    actualSummaryState,
    engagementData,
    week,
    resourceId,
    form,
    name,
  ]);

  useEffect(() => {
    if (!!matterId && !!resourceId && !!week && !actualToDate) {
      refetch();
    }
  }, [matterId, resourceId, week, actualToDate, refetch]);

  return (
    <Spin spinning={false}>
      {/* See comment in BudgetRender above - same pattern: hidden
          Form.Item keeps the value in the form model, visible <span>
          avoids antd's Form.Item wrapper markup that was causing the
          layout gap. */}
      <Form.Item name={name} className="atb-hidden-field">
        <Input
          disabled
          placeholder="-"
          type="number"
          className="actual-value-input"
        />
      </Form.Item>
      <span className="atb-text">{actualToDate ?? "-"}</span>
    </Spin>
  );
};

const ChargeAbleRender = ({
  editable,
  name,
  date,
  holidays,
  leaves,
  status,
  timesheetgetdata,
}) => {
  const form = Form.useFormInstance();
  const saturdayORSunday = name[1] === "sat" || name[1] === "sun";
  const client = Form.useWatch([name[0], "client"], form);
  const userInfo = useSelector(getUserInfo);
  const focusNextChargable = () => {
    const nextDay =
      name[1] === "mon"
        ? "tue"
        : name[1] === "tue"
          ? "wed"
          : name[1] === "wed"
            ? "thu"
            : name[1] === "thu"
              ? "fri"
              : name[1] === "fri"
                ? "sat"
                : name[1] === "sat"
                  ? "sun"
                  : "task";

    form.getFieldInstance([name[0], nextDay]).focus();
  };

  const focusPreviousChargable = () => {
    const previousDay =
      name[1] === "sun"
        ? "sat"
        : name[1] === "sat"
          ? "fri"
          : name[1] === "fri"
            ? "thu"
            : name[1] === "thu"
              ? "wed"
              : name[1] === "wed"
                ? "tue"
                : name[1] === "tue"
                  ? "mon"
                  : "engagement";

    form.getFieldInstance([name[0], previousDay]).focus();
  };

  const handleChargeAmountChange = (e) => {
    let value = e.target.value;

    // Convert the value to a number to remove leading zeros
    // value = Number(value);
    // value = parseInt(value, 10);
    //
    //     if (value > 12) {
    //       value = 12;
    //     }

    form.setFieldsValue({
      [name[0]]: {
        [name[1]]: value,
      },
    });
  };

  const IsInBetweenHolidays = (date) => {
    const targetDate = new Date(date);

    return holidays?.some((holiday) => {
      const startDate = new Date(holiday.start_date);
      const endDate = new Date(holiday.end_date);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      return targetDate >= startDate && targetDate <= endDate;
    });
  };

  const IsInBetweenLeaves = (date) => {
    const targetDate = new Date(date);
    const filterData = leaves?.filter(
      (data) =>
        data?.userid === (timesheetgetdata?.employeeId || userInfo?.userid)
    );
    if (client === "ap") {
    }
    return filterData?.some((leave) => {
      const startDate = new Date(leave.start_date);
      const endDate = new Date(leave.end_date);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      const matched = targetDate >= startDate && targetDate <= endDate;
      if (client === "ap") {
      }
      return matched;
    });
  };

  const getLeaveInfo = (date) => {
    const targetDate = new Date(date);
    const filterData = leaves?.filter(
      (data) =>
        data?.userid === (timesheetgetdata?.employeeId || userInfo?.userid)
    );
    const leave = filterData?.find((leave) => {
      const startDate = new Date(leave.start_date);
      const endDate = new Date(leave.end_date);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      return targetDate >= startDate && targetDate <= endDate;
    });

    if (client === "ap") {
    }

    if (!leave) return null;

    const leaveEndDate = new Date(leave.end_date);
    leaveEndDate.setHours(0, 0, 0, 0);

    const isLastDay = targetDate.getTime() === leaveEndDate.getTime();
    const isHalfDayOnLastDay =
      isLastDay && parseFloat(leave.number_of_days) % 1 !== 0;

    if (client === "ap") {
    }

    return {
      ...leave,
      isHalfDayOnLastDay,
    };
  };

  const isHoliday = IsInBetweenHolidays(date);
  const isLeave = IsInBetweenLeaves(date);
  const leaveInfo = getLeaveInfo(date);
  const isHalfDayOnLastDay = leaveInfo?.isHalfDayOnLastDay;
  const isClientAP = client === "ap";
  useEffect(() => {
    if (isClientAP) {
      if (saturdayORSunday) {
        form.setFieldsValue({
          [name[0]]: { [name[1]]: 0 },
        });
      } else if (leaveInfo?.isHalfDayOnLastDay) {
        form.setFieldsValue({
          [name[0]]: { [name[1]]: 4 },
        });
      } else if (isHoliday || isLeave) {
        form.setFieldsValue({
          [name[0]]: { [name[1]]: 8 },
        });
      }
    }
  }, [form, isHoliday, isLeave, isClientAP, name, leaveInfo]);

  const formValues = Form.useWatch([], form) || {};

  const totalDayHours = Object.values(formValues)
    ?.map((entry) => Number(entry?.[name[1]]) || 0)
    ?.reduce((sum, val) => sum + val, 0);

  const isFullyUtilized = totalDayHours >= 8;
  const isClientNC = client === "nc";
  const isClientC = client && client !== "ap" && client !== "nc"; // any other client is "c"
  const isDraft = status === "draft" && timesheetgetdata?.timeSheet?.length > 0;
  const isFieldDisabled =
    // Allow editing if client is "nc" or "c" and status is "draft"
    !(isDraft && (isClientNC || isClientC))
      ? !editable ||
      isClientAP ||
      (isClientAP && saturdayORSunday) ||
      (!saturdayORSunday && (isHoliday || (isLeave && !isHalfDayOnLastDay)))
      : false;

  // const isFieldDisabled =
  //    !editable ||
  //       isClientAP ||
  //       (isClientAP && saturdayORSunday) || // Disable Sat/Sun only if client is 'ap'
  //       (!saturdayORSunday && (isHoliday || (isLeave && !isHalfDayOnLastDay)));

  return (
    <Form.Item
      name={name}
      rules={[{ required: !saturdayORSunday, message: "Required" }]}
    >
      <Input
        placeholder="0"
        disabled={isFieldDisabled}
        onChange={handleChargeAmountChange}
        onKeyDown={(e) => {
          if (e.keyCode === 9 || e.keyCode === 13) focusNextChargable();
          if (e.shiftKey && e.keyCode === 9) focusPreviousChargable();

          const isNumberKey =
            (e.keyCode > 95 && e.keyCode < 106) || // Numpad numbers
            (e.keyCode > 47 && e.keyCode < 58); // Numbers

          const isControlKey =
            e.keyCode === 8 || // Backspace
            e.keyCode === 46 || // Delete
            (e.keyCode >= 37 && e.keyCode <= 40); // Arrow keys

          const isDecimalPoint =
            e.key === "." && !e.target.value?.includes("."); // Decimal point

          if (!(isNumberKey || isControlKey || isDecimalPoint)) {
            e.preventDefault ? e.preventDefault() : (e.returnValue = false);
          }
        }}
      />
    </Form.Item>
  );
};

const claimsColumns = [
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    width: 180,
  },
  {
    title: "VAT/NON-VAT",
    dataIndex: "vat",
    key: "vat",
    width: 110,
  },
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
    width: 120,
  },
  {
    title: "Currency",
    dataIndex: "currency",
    key: "currency",
    width: 100,
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    width: 100,
  },
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    width: 120,
  },
  {
    title: "Attachment",
    dataIndex: "attachment",
    key: "attachment",
    width: 120,
  },
  {
    title: "Remarks",
    dataIndex: "remarks",
    key: "remarks",
    width: 160,
  },
  {
    title: "Actions",
    dataIndex: "actions",
    key: "actions",
    width: 70,
  },
];

const ClaimDescriptionRender = ({ claimsIndex, timesheetIndex, editable }) => {
  return (
    <Form.Item
      name={[timesheetIndex, "claims", claimsIndex, "description"]}
      rules={[
        {
          required: true,
          message: "Required",
        },
      ]}
    >
      <Input placeholder="Enter" className="start" disabled={!editable} />
    </Form.Item>
  );
};

const ClaimVatRender = ({ claimsIndex, timesheetIndex, editable }) => {
  const form = Form.useFormInstance();

  // any one of the checkbox should be checked
  const validateVat = () => {
    const vat = form.getFieldValue([
      timesheetIndex,
      "claims",
      claimsIndex,
      "vat",
    ]);
    const nonVat = form.getFieldValue([
      timesheetIndex,
      "claims",
      claimsIndex,
      "non_vat",
    ]);

    if (!vat && !nonVat) {
      return Promise.reject("Required");
    }

    form.setFields([
      {
        name: [timesheetIndex, "claims", claimsIndex, "vat"],
        errors: [],
      },
      {
        name: [timesheetIndex, "claims", claimsIndex, "non_vat"],
        errors: [],
      },
    ]);
    return Promise.resolve();
  };

  return (
    <>
      <Form.Item
        name={[timesheetIndex, "claims", claimsIndex, "vat"]}
        className="mb-0"
        valuePropName="checked"
        rules={[
          {
            validator: validateVat,
          },
        ]}
      >
        <Checkbox
          disabled={!editable}
          onChange={(e) => {
            // if vat is checked then non-vat should be unchecked
            if (e.target.checked) {
              form.setFieldsValue({
                [timesheetIndex]: {
                  claims: {
                    [claimsIndex]: {
                      non_vat: false,
                    },
                  },
                },
              });
            }
          }}
        >
          VAT
        </Checkbox>
      </Form.Item>

      <Form.Item
        name={[timesheetIndex, "claims", claimsIndex, "non_vat"]}
        className="mb-0"
        valuePropName="checked"
        rules={[
          {
            validator: validateVat,
          },
        ]}
      >
        <Checkbox
          disabled={!editable}
          value="non-vat"
          className="ms-0"
          onChange={(e) => {
            // if non-vat is checked then vat should be unchecked
            if (e.target.checked) {
              form.setFieldsValue({
                [timesheetIndex]: {
                  claims: {
                    [claimsIndex]: {
                      vat: false,
                    },
                  },
                },
              });
            }
          }}
        >
          NON-VAT
        </Checkbox>
      </Form.Item>
    </>
  );
};

const ClaimTypeRender = ({ claimsIndex, timesheetIndex, editable }) => {
  const claimTypes = useMemo(
    () => [
      {
        label: "Medical",
        value: "Medical",
      },
      {
        label: "Travel",
        value: "Travel",
      },
      {
        label: "Stationery",
        value: "Stationery",
      },
      {
        label: "Food",
        value: "Food",
      },
      {
        label: "Other",
        value: "Other",
      },
      // {
      //   label: "Disbursement",
      //   value: "Disbursement",
      // },

      {
        label: "Subcontract",
        value: "Subcontract",
      },
      // {
      //   label: "Disbursement",
      //   value: "Disbursement",
      // },
      // {
      //   label: "DNV",
      //   value: "DNV",
      // },
    ],
    []
  );

  return (
    <Form.Item
      name={[timesheetIndex, "claims", claimsIndex, "type"]}
      rules={[
        {
          required: true,
          message: "Required",
        },
      ]}
    >
      <Select
        disabled={!editable}
        placeholder="Select"
        options={claimTypes}
        style={{ flex: 1, width: "100%" }}
      />
    </Form.Item>
  );
};

const ClaimCurrencyRender = ({
  claimsIndex,
  timesheetIndex,
  currency,
  engagements,
  matterId,
}) => {
  const form = Form.useFormInstance();

  useEffect(() => {
    if (currency) {
      form.setFieldsValue({
        [timesheetIndex]: {
          claims: {
            [claimsIndex]: {
              currency: currency,
              resourceId: engagements?.find(
                (e) => e?.matterId === matterId
              )?.resourceId,
            },
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  return (
    <Form.Item
      name={[timesheetIndex, "claims", claimsIndex, "currency"]}
      rules={[
        {
          required: true,
          message: "Required",
        },
      ]}
    >
      <Input placeholder="Enter" className="start" disabled />
    </Form.Item>
  );
};

const ClaimAmountRender = ({ claimsIndex, timesheetIndex, editable }) => {
  return (
    <Form.Item
      name={[timesheetIndex, "claims", claimsIndex, "amount"]}
      rules={[
        {
          required: true,
          message: "Required",
        },
      ]}
    >
      <Input
        placeholder="0"
        onKeyDown={allowOnlyNumbers}
        disabled={!editable}
      />
    </Form.Item>
  );
};

const ClaimDateRender = ({ claimsIndex, timesheetIndex, editable }) => {
  return (
    <Form.Item
      name={[timesheetIndex, "claims", claimsIndex, "date"]}
      getValueProps={(value) => ({ value: value ? moment(value) : null })}
      rules={[
        {
          required: true,
          message: "Required",
        },
      ]}
    >
      <DatePicker
        placeholder="Select"
        style={{ flex: 1, width: "100%" }}
        format="DD-MM-YYYY"
        disabled={!editable}
      />
    </Form.Item>
  );
};

const AttachmentRender = ({ claimsIndex, timesheetIndex, editable }) => {
  const { key, url } = queryKeys.postClaimsAttachment;

  const { mutate: uploadAttachement } = useMutate(key, url);
  const form = Form.useFormInstance();

  const attachment = Form.useWatch(
    [timesheetIndex, "claims", claimsIndex, "attachment"],
    form
  );

  useEffect(() => {
    if (!attachment) {
      // Clear attachment fields if not populated
      form.setFieldsValue({
        [timesheetIndex]: {
          claims: {
            [claimsIndex]: {
              attachment: [],
            },
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment]);

  const handleUploadAttachement = async (file) => {
    const fileData = file.fileList[0].originFileObj;

    const base64Data = await fileToBase64(fileData);

    const base64DataSplit = base64Data?.split(",")?.[1];

    const data = {
      filename: fileData.name,
      filetype: fileData.type,
      content: base64DataSplit,
    };

    uploadAttachement(data, {
      onSuccess: (res) => {
        form.setFieldsValue({
          [timesheetIndex]: {
            claims: {
              [claimsIndex]: {
                attachment: [
                  {
                    url: res?.fileUrl,
                    name: fileData.name,
                  },
                ],
              },
            },
          },
        });

        notification.success({
          message: "Attachment uploaded successfully",
        });
      },
    });
  };

  const handlePreview = (fileObj) => {
    if (!fileObj.originFileObj) {
      notification.error({
        message: "No file to preview",
      });
      return;
    }

    const file = fileObj.originFileObj;
    const fileUrl = URL.createObjectURL(file);

    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(fileUrl);
  };

  return (
    <Form.Item
      name={[timesheetIndex, "claims", claimsIndex, "attachment"]}
      valuePropName="fileList"
      getValueFromEvent={normFile}
      multiple={false}
    >
      <Upload
        beforeUpload={() => false}
        onChange={(file) => handleUploadAttachement(file)}
        maxCount={1}
        multiple={false}
        accept="image/*,.png,.jpg,.jpeg,.webp,.jpeg"
        showUploadList={{
          showRemoveIcon: false,
        }}
        onPreview={handlePreview}
        disabled={!editable}
      >
        <Button
          style={{
            width: "100%",
          }}
          icon={<UploadOutlined />}
          disabled={!editable}
        >
          Upload
        </Button>
      </Upload>
    </Form.Item>
  );
};

const ClaimRemarksRender = ({ claimsIndex, timesheetIndex, editable }) => {
  return (
    <Form.Item name={[timesheetIndex, "claims", claimsIndex, "remarks"]}>
      <Input placeholder="Enter" className="start" disabled={!editable} />
    </Form.Item>
  );
};

const ClaimActionsRender = ({
  timesheetIndex,
  claimsIndex,
  editable,
  handleClearClaimFn,
  timesheetClaimsIds,
}) => {
  const { key: deleteKey, url: deleteUrl } = queryKeys.deleteTimesheetClaim;
  const { mutate: deleteClaim } = useDelete(deleteKey, deleteUrl);

  const handleDelete = () => {
    if (timesheetClaimsIds) {
      deleteClaim(
        { timesheetClaimsIds: [timesheetClaimsIds] },
        {
          onSuccess: () => {
            handleClearClaimFn(timesheetIndex, claimsIndex);
          },
          onError: (err) => {
            console.error("Delete claim failed:", err);
            notification.error({ message: "Failed to delete claim. Please try again." });
          },
        }
      );
    } else {
      handleClearClaimFn(timesheetIndex, claimsIndex);
    }
  };

  return (
    editable && (
      <div
        onClick={handleDelete}
        style={{
          marginTop: "-16px",
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <ClearIcon />
      </div>
    )
  );
};

const ClaimsRender = ({
  editable,
  index: timesheetIndex,
  claims,
  extraActions,
  values,
  engagements,
}) => {
  const [showClaimModal, setShowClaimModal] = React.useState(false);

  const form = Form.useFormInstance();
  const {
    handleAddMoreClaim,
    handleClearClaim,
    handleResetClaim,
    handleRemoveClaim,
  } = extraActions || {};

  const matterId = Form.useWatch([timesheetIndex, "engagement"], form);

  const { key, url } = queryKeys.getMatterById(matterId);
  const { isFetched } = useQueryState(key);
  const { data: engagementDataRes, isLoading: isEngagmentLoading } = useFetch(
    key,
    url,
    {
      enabled:
        !isFetched &&
        !!matterId &&
        matterId !== "nc" &&
        matterId !== "ap",
    }
  );

  const engagementData = engagementDataRes?.data;
  const currency = engagementData?.financial_resourcePlan?.currency;

  const { key: deleteKey, url: deleteUrl } = queryKeys.deleteTimesheetClaim;
  const { mutate: deleteClaim } = useDelete(deleteKey, deleteUrl);

  const claimsArray =
    Array.isArray(claims) && typeof claims !== "number"
      ? claims
      : [initialClaim];

  const handleClearClaimFn = (timeSheetIndex, claimIndex) => {
    handleClearClaim(timeSheetIndex, claimIndex);
    if (claimIndex === 0) {
      handleRemoveClaim(timeSheetIndex);
      setShowClaimModal(false);
    }
  };

  const claimsData = claimsArray?.map((claim, index) => {
    return {
      key: index,
      description: (
        <ClaimDescriptionRender
          claimsIndex={index}
          timesheetIndex={timesheetIndex}
          editable={editable}
        />
      ),
      vat: (
        <ClaimVatRender
          claimsIndex={index}
          timesheetIndex={timesheetIndex}
          editable={editable}
        />
      ),
      type: (
        <ClaimTypeRender
          claimsIndex={index}
          timesheetIndex={timesheetIndex}
          editable={editable}
        />
      ),
      currency: (
        <ClaimCurrencyRender
          claimsIndex={index}
          timesheetIndex={timesheetIndex}
          editable={editable}
          currency={currency}
          values={values}
          matterId={matterId}
          engagements={engagements}
        />
      ),
      amount: (
        <ClaimAmountRender
          claimsIndex={index}
          timesheetIndex={timesheetIndex}
          editable={editable}
        />
      ),
      date: (
        <ClaimDateRender
          claimsIndex={index}
          timesheetIndex={timesheetIndex}
          editable={editable}
        />
      ),
      attachment: (
        <AttachmentRender
          claimsIndex={index}
          timesheetIndex={timesheetIndex}
          editable={editable}
        />
      ),
      remarks: (
        <ClaimRemarksRender
          claimsIndex={index}
          timesheetIndex={timesheetIndex}
          editable={editable}
        />
      ),
      actions: (
        <ClaimActionsRender
          timesheetIndex={timesheetIndex}
          claimsIndex={index}
          editable={editable}
          handleClearClaimFn={handleClearClaimFn}
          timesheetClaimsIds={claim?.claimsId}
        />
      ),
    };
  });

  const hasClaims = claimsArray?.length > 0;

  const handleOpenClaimModal = () => {
    if (editable && !hasClaims) {
      handleAddMoreClaim(timesheetIndex);
    }
    setShowClaimModal(true);
  };

  const handleCloseClaimModal = () => {
    setShowClaimModal(false);
  };

  const handleAddMoreClaimClick = () => {
    handleAddMoreClaim(timesheetIndex);
  };

  const handleSaveClaims = async () => {
    try {
      let hasClaimsError = false;
      for (let index = 0; index < claimsArray.length; index++) {
        const fieldsToValidate = [
          [timesheetIndex, "claims", index, "description"],
          [timesheetIndex, "claims", index, "vat"],
          [timesheetIndex, "claims", index, "non_vat"],
          [timesheetIndex, "claims", index, "type"],
          [timesheetIndex, "claims", index, "currency"],
          [timesheetIndex, "claims", index, "amount"],
          [timesheetIndex, "claims", index, "date"],
        ];
        try {
          await form.validateFields(fieldsToValidate);
        } catch (error) {
          hasClaimsError = true;
          break;
        }
      }
      if (hasClaimsError) return;
      handleCloseClaimModal();
    } catch (error) {
      // Handle form-level validation errors if needed
    }
  };

  const handleResetClaimClick = () => {
    const allClaimIds = claimsArray
      ?.map((claim) => claim?.claimsId)
      ?.filter(Boolean);

    if (allClaimIds.length > 0) {
      deleteClaim(
        { timesheetClaimsIds: allClaimIds },
        {
          onSuccess: () => {
            handleResetClaim(timesheetIndex);
          },
          onError: (err) => {
            console.error("Reset claims failed:", err);
            notification.error({ message: "Failed to reset claims. Please try again." });
          },
        }
      );
    } else {
      handleResetClaim(timesheetIndex);
    }
  };

  return (
    <>
      <Button
        className="add-claims"
        onClick={handleOpenClaimModal}
        disabled={
          matterId === "nc" ||
          matterId === "ap" ||
          matterId === null ||
          matterId === undefined
        }
      >
        {editable ? "Add" : "View"} Claims : (
        {Array.isArray(claims) ? claims.length : 0})
      </Button>

      <StyledModal
        open={showClaimModal}
        onCancel={handleCloseClaimModal}
        title={"Add Claims"}
        width={1200}
        footer={null}
        closable={true}
        maskClosable={false}
        forceRender={true}
      >
        <ClaimsTable
          columns={claimsColumns}
          dataSource={claimsData}
          pagination={false}
          bordered={true}
        />

        <div className="mt-2 d-flex justify-content-between align-items-center">
          {editable && (
            <Button variant="text" onClick={handleAddMoreClaimClick}>
              + Add More
            </Button>
          )}
          {editable && (
            <div className="d-flex align-items-center gap-2">
              <Button type="primary" onClick={handleResetClaimClick}>
                Reset
              </Button>
              <Button type="primary" onClick={handleSaveClaims}>
                Save
              </Button>
            </div>
          )}
        </div>
      </StyledModal>
    </>
  );
};

const RemarksRender = ({
  editable,
  name,
  resources,
  holidays,
  leaves,
  data,
  timesheetgetdata,
}) => {
  const form = Form.useFormInstance();
  const client = Form.useWatch([name[0], "client"], form);
  const formValues = Form.useWatch([], form) || {};
  const userInfo = useSelector(getUserInfo);
  const isInBetweenHolidays = () => {
    const currentWeekStartDate = moment(
      getWeekStartDate(data?.[0]?.currentWeek),
      "YYYY-MM-DD"
    );
    const weekStart = moment(currentWeekStartDate, "YYYY-MM-DD");
    let holidayNames = [];
    for (let i = 0; i < 7; i++) {
      const currentDate = weekStart
        .clone()
        .add(i, "days")
        .startOf("day")
        .toDate();
      holidays?.forEach((holiday) => {
        const startDate = moment(holiday.start_date, "YYYY-MM-DD")
          .startOf("day")
          .toDate();
        const endDate = moment(holiday.end_date, "YYYY-MM-DD")
          .endOf("day")
          .toDate();
        if (currentDate >= startDate && currentDate <= endDate) {
          if (!holidayNames.includes(holiday.holiday_name)) {
            holidayNames.push(holiday.holiday_name);
          }
        }
      });
    }
    return holidayNames.join(" / ");
  };

  const isLeaveInWeek = () => {
    const currentWeekStartDate = moment(
      getWeekStartDate(data?.[0]?.currentWeek),
      "YYYY-MM-DD"
    );
    const weekStart = moment(currentWeekStartDate, "YYYY-MM-DD");
    for (let i = 0; i < 7; i++) {
      const currentDate = weekStart
        .clone()
        .add(i, "days")
        .startOf("day")
        .toDate();
      const filterData = leaves?.filter(
        (data) =>
          data?.userid === (timesheetgetdata?.employeeId || userInfo?.userid)
      );
      const isLeaveDay = filterData?.some((leave) => {
        const startDate = moment(leave.start_date, "YYYY-MM-DD")
          .startOf("day")
          .toDate();
        const endDate = moment(leave.end_date, "YYYY-MM-DD")
          .startOf("day")
          .toDate();
        const matched = currentDate >= startDate && currentDate <= endDate;
        return matched;
      });
      if (isLeaveDay) {
        return true;
      }
    }
    return false;
  };

  const leaveReason = useMemo(() => leaves?.[0]?.reason || "", [leaves]);
  const currentValue = form.getFieldValue(name);

  useEffect(() => {
    const row = formValues?.[name[0]];
    if (!form || !name) return;
    if ((holidays?.length || 0) > 0 || (leaves?.length || 0) > 0) {
      const holidayNames = isInBetweenHolidays();
      const isLeaveInWeekStatus = isLeaveInWeek();
      if (row?.client === "ap") {
        if (holidayNames && isLeaveInWeekStatus) {
          form.setFieldValue(name, holidayNames + " / " + leaveReason);
        } else if (holidayNames) {
          form.setFieldValue(name, holidayNames);
        } else if (isLeaveInWeekStatus) {
          form.setFieldValue(name, leaveReason);
        }
      }
    }
  }, [client, holidays, leaveReason, formValues, name, form]);
  return (
    <Form.Item name={name} rules={[{ required: true, message: "Required" }]}>
      <Input.TextArea
        className="timesheet_textarea_input"
        placeholder="Enter Remarks"
        disabled={!editable || client === "ap"} // Disable if client is "ap"
        style={{
          textAlign: "start",
          verticalAlign: "top",
          lineHeight: "1.5", // Try adjusting this
        }}
        maxLength={400}
        showCount
      />
    </Form.Item>
  );
};

const NCClientRender = ({ editable, name, clientOptions, containerRef }) => {
  const form = Form.useFormInstance();

  const client = Form.useWatch([name[0], "client"], form);
  const isNonChargeable = client === "nc";

  useEffect(() => {
    if (isNonChargeable) {
      form.setFieldValue(name, "nc");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNonChargeable]);

  const nCClientOptions = isNonChargeable
    ? [
      {
        label: "Non Chargeable",
        value: "nc",
        isLoaded: true,
      },
    ]
    : [
      {
        label: "--",
        value: "-",
        isLoaded: true,
      },
    ];

  return (
    <Form.Item name={name}>
      <Select
        placeholder="Select NC Client"
        allowClear={false}
        style={{ flex: 1, width: "100%" }}
        options={nCClientOptions?.map((client) => {
          return {
            label: client.isLoaded ? (
              client.label
            ) : (
              <>
                <Spin /> Loading{" "}
              </>
            ),
            value: client.value,
          };
        })}
        getPopupContainer={() => containerRef.current}
        disabled={true}
      ></Select>
    </Form.Item>
  );
};

const SubEngagementRender = ({ editable, name }) => {
  return (
    <Form.Item name={name} label="Sub Engagement">
      <Select
        placeholder="Select"
        allowClear={true}
        style={{ flex: 1, width: "100%" }}
        options={[
          {
            label: "--",
            value: "-",
          },
        ]}
        disabled={!editable}
      ></Select>
    </Form.Item>
  );
};

const getSumBoxColorClass = (client, status, allRowsApproved, isViewMode) => {
  if (isViewMode) {
    if (!allRowsApproved) return "";
    if (!client) return "";
    if (client === "ap") return " absence-total";
    if (client === "nc") return " blue";
    return " green";
  }
  if (status === "pending" || status === "declined") return "";
  if (!client) return "";
  if (client === "ap") return " absence-total";
  if (client === "nc") return status === "approved" ? "" : " blue";
  return " green";
};

const SumRender = ({ name, status, allRowsApproved, editable, client: clientProp, isEditable }) => {
  const form = Form.useFormInstance();
  const watchedClient = Form.useWatch([name[0], "client"], form);
  // Editable rows have a live Form.Item for "client", so trust it fully
  // (including undefined after the user clears the select). Locked/
  // read-only rows never mount that Form.Item, so watchedClient is always
  // undefined there regardless of the row's real client - use the static
  // prop instead.
  const client = isEditable ? watchedClient : clientProp;
  const sumValue = Form.useWatch(name, form);

  useEffect(() => {
    const mon = form.getFieldValue([name[0], "mon"]);
    const tue = form.getFieldValue([name[0], "tue"]);
    const wed = form.getFieldValue([name[0], "wed"]);
    const thu = form.getFieldValue([name[0], "thu"]);
    const fri = form.getFieldValue([name[0], "fri"]);
    const sat = form.getFieldValue([name[0], "sat"]);
    const sun = form.getFieldValue([name[0], "sun"]);

    const sum =
      (parseFloat(mon) || 0) +
      (parseFloat(tue) || 0) +
      (parseFloat(wed) || 0) +
      (parseFloat(thu) || 0) +
      (parseFloat(fri) || 0) +
      (parseFloat(sat) || 0) +
      (parseFloat(sun) || 0);

    form.setFieldsValue({
      [name[0]]: {
        sum: sum,
      },
    });
  }, [form, name]);

  return (
    <>
      {/* Keeps the value in the form model; not shown visually */}
      <Form.Item name={name} className="sum-hidden-field">
        <Input disabled placeholder="0" type="number" />
      </Form.Item>
      <div
        className={`sum-box${getSumBoxColorClass(client, status, allRowsApproved, !editable)}`}
      >
        <span className="sum-value">{sumValue || 0}</span>
        <span className="sum-label">Hrs</span>
      </div>
    </>
  );
};

const ExtraSectionRender = ({
  index,
  clientOptions,
  declined,
  editable,
  containerRef,
  holidays,
  leaves,
  resources,
  data,
  timesheetgetdata,
}) => {
  return (
    <>
      <div className="field">
        <label className="field-label">Task Type</label>
        <TaskRender
          editable={editable}
          name={[index, "task"]}
          containerRef={containerRef}
          holidays={holidays}
          leaves={leaves}
          data={data}
          timesheetgetdata={timesheetgetdata}
        />
      </div>

      <div className="field remarks-field">
        <label className="field-label">Remarks</label>
        <RemarksRender
          editable={editable}
          name={[index, "remarks"]}
          resources={resources}
          holidays={holidays}
          leaves={leaves}
          data={data}
          timesheetgetdata={timesheetgetdata}
        />
      </div>

      {declined && (
        <div className="declined-reason">
          Note: {declined?.reason || "No reason provided"}
        </div>
      )}
    </>
  );
};

const TypeRender = ({ index, client: clientProp }) => {
  const form = Form.useFormInstance();

  const watchedClient = Form.useWatch([index, "client"], form);
  const client = clientProp ?? watchedClient;

  if (!client) {
    return <span></span>;
  } else if (client === "nc" || client === "ap") {
    return <span className="type-tag non-chargeable">Non Chargeable</span>;
  } else {
    return (
      <span className="type-tag chargeable">
        <span className="type-dot" />
        Chargeable
      </span>
    );
  }
};

const ActionRender = ({
  editable,
  status,
  index,
  approval,
  extraActions,
  personInCharge,
  actualStatus,
  values,
}) => {
  const userInfo = useSelector(getUserInfo);
  const form = Form.useFormInstance();
  const userId = userInfo?.userid;
  const client = Form.useWatch([index, "client"], form); // Watch the client field

  const [showDeclineModal, setShowDeclineModal] = React.useState(false);

  const { handleClearTimesheet } = extraActions || {};

  if (status === "declined" || status === "approved" || status === "pending") {
    // Skip
  } else if (approval && personInCharge === userId) {
    return (
      <div className="action-buttons">
        {/* <ApproveActionButton index={index} /> */}
        {/* <CloseIcon
          onClick={() => {
            setShowDeclineModal(true);
          }}
          title="Decline Timesheet"
        />

        <DeclineTimesheetModal
          open={showDeclineModal}
          setOpen={setShowDeclineModal}
          index={index}
        /> */}
      </div>
    );
  }
  const isEditableRow = (editable && client !== "ap") || status === "draft";

  if (isEditableRow) {
    if (client === "ap") return null;

    return (
      <div className="action">
        <span
          className="icon-btn"
          onClick={() => handleClearTimesheet(index, values)}
        >
          <DeleteOutlined />
        </span>
      </div>
    );
  }

  return (
    <span>
      <TimeSheetStatus status={status} />
    </span>
  );
};

const TotalRender = ({ total, min, max, isSatOrSun = false }) => {
  const status =
    isSatOrSun && total === 0
      ? "normal"
      : total < min || total > max
        ? "error"
        : "good";

  return (
    <div className={`total ${status}`}>
      <span>{Number(total.toFixed(2))}</span>
    </div>
  );
};

const useTimeSheetDetails = (
  data,
  editable,
  approval,
  clientOptions,
  engagements,
  extraActions,
  containerRef,
  holidays,
  leaves,
  timesheetgetdata,
  selectedTabId,
  resources,
  editables,
  week,
  editingIndex
) => {
  const userInfo = useSelector(getUserInfo);
  const currentWeekStartDate = getWeekStartDate(data[0]?.currentWeek);

  const monday = moment(currentWeekStartDate, "YYYY-MM-DD");
  const tuesday = moment(currentWeekStartDate, "YYYY-MM-DD").add(1, "days");
  const wednesday = moment(currentWeekStartDate, "YYYY-MM-DD").add(2, "days");
  const thursday = moment(currentWeekStartDate, "YYYY-MM-DD").add(3, "days");
  const friday = moment(currentWeekStartDate, "YYYY-MM-DD").add(4, "days");
  const saturday = moment(currentWeekStartDate, "YYYY-MM-DD").add(5, "days");
  const sunday = moment(currentWeekStartDate, "YYYY-MM-DD").add(6, "days");

  const form = Form.useFormInstance();

  const values = Form.useWatch([], form) || {};

  const chargeableDays = Object.keys(values).map((key) => {
    return {
      mon: parseFloat(values[key]?.mon) || 0,
      tue: parseFloat(values[key]?.tue) || 0,
      wed: parseFloat(values[key]?.wed) || 0,
      thu: parseFloat(values[key]?.thu) || 0,
      fri: parseFloat(values[key]?.fri) || 0,
      sat: parseFloat(values[key]?.sat) || 0,
      sun: parseFloat(values[key]?.sun) || 0,
    };
  });
  const allMondayTotal = sumBy(chargeableDays, (item) => item.mon);
  const allTuesdayTotal = sumBy(chargeableDays, (item) => item.tue);
  const allWednesdayTotal = sumBy(chargeableDays, (item) => item.wed);
  const allThursdayTotal = sumBy(chargeableDays, (item) => item.thu);
  const allFridayTotal = sumBy(chargeableDays, (item) => item.fri);
  const allSaturdayTotal = sumBy(chargeableDays, (item) => item.sat);
  const allSundayTotal = sumBy(chargeableDays, (item) => item.sun);

  const allTotal =
    (parseFloat(allMondayTotal) || 0) +
    (parseFloat(allTuesdayTotal) || 0) +
    (parseFloat(allWednesdayTotal) || 0) +
    (parseFloat(allThursdayTotal) || 0) +
    (parseFloat(allFridayTotal) || 0) +
    (parseFloat(allSaturdayTotal) || 0) +
    (parseFloat(allSundayTotal) || 0);

  const [selectedTimesheetKeys, setSelectedTimesheetKeys] = useState([]);

  const handleOnChange = (record, checked) => {
    setSelectedTimesheetKeys((prev) =>
      checked ? [...prev, record.key] : prev.filter((key) => key !== record.key)
    );
  };

  const columns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 60,
      render: (_, record) =>
        approval ? (
          record?.action?.props?.status === "approved" ? (
            <CheckSquareFilled className="approve-checkbox-icon approved" />
          ) : record?.action?.props?.status === "declined" ? (
            <CloseSquareFilled className="approve-checkbox-icon declined" />
          ) : (
            <Checkbox
              className="approve-checkbox"
              disabled={
                record?.action?.props?.status === "approved" ||
                record?.action?.props?.status === "declined" ||
                record?.action?.props?.personInCharge !== userInfo?.userid
              }
              checked={
                selectedTimesheetKeys?.includes(record?.key) ||
                record?.action?.props?.status === "approved" ||
                record?.action?.props?.status === "declined"
              }
              onChange={(e) => handleOnChange(record, e.target.checked)}
            />
          )
        ) : null,
    },
  ];

  const actionColumn = {
    title: "Action",
    dataIndex: "action",
    key: "action",
    width: 95,
  };

  if (approval) {
    columns.push(actionColumn);
  }

  columns.push(
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      children: [
        {
          title: "Client",
          dataIndex: "client",
          ellipsis: {
            showTitle: true,
          },
          key: "client",
          width: 160,
        },
        {
          title: (
            <div>
              <div>Job /</div>
              <div>Engagement</div>
            </div>
          ),
          dataIndex: "engagement",
          key: "engagement",
          width: 160,
        },
        {
          title: "Budget (hr)",
          dataIndex: "budget",
          key: "budget",
          width: 65,
        },
        {
          title: "Actual to date",
          dataIndex: "actual_to_date",
          key: "actual_to_date",
          width: 65,
        },
      ],
    },
    {
      title: (
        <TotalRender
          total={allMondayTotal}
          min={MIN_WORKING_HOURS}
          max={MAX_WORKING_HOURS}
        />
      ),
      dataIndex: "mondayTotal",
      align: "center",
      key: "mondayTotal",
      children: [
        {
          title: DayRender("Mon", monday.format("D"), monday.format("MMM")),
          dataIndex: "mon",
          key: "mon",
          width: 65,
        },
      ],
    },
    {
      title: (
        <TotalRender
          total={allTuesdayTotal}
          min={MIN_WORKING_HOURS}
          max={MAX_WORKING_HOURS}
        />
      ),
      dataIndex: "tuesdayTotal",
      key: "tuesdayTotal",
      children: [
        {
          title: DayRender("Tue", tuesday.format("D"), tuesday.format("MMM")),
          dataIndex: "tue",
          key: "tue",
          width: 65,
        },
      ],
    },
    {
      title: (
        <TotalRender
          total={allWednesdayTotal}
          min={MIN_WORKING_HOURS}
          max={MAX_WORKING_HOURS}
        />
      ),
      dataIndex: "wednesdayTotal",
      key: "wednesdayTotal",
      children: [
        {
          title: DayRender(
            "Wed",
            wednesday.format("D"),
            wednesday.format("MMM")
          ),
          dataIndex: "wed",
          key: "wed",
          width: 65,
        },
      ],
    },
    {
      title: (
        <TotalRender
          total={allThursdayTotal}
          min={MIN_WORKING_HOURS}
          max={MAX_WORKING_HOURS}
        />
      ),
      dataIndex: "thursdayTotal",
      key: "thursdayTotal",
      children: [
        {
          title: DayRender("Thu", thursday.format("D"), thursday.format("MMM")),
          dataIndex: "thu",
          key: "thu",
          width: 65,
        },
      ],
    },
    {
      title: (
        <TotalRender
          total={allFridayTotal}
          min={MIN_WORKING_HOURS}
          max={MAX_WORKING_HOURS}
        />
      ),
      dataIndex: "fridayTotal",
      key: "fridayTotal",
      children: [
        {
          title: DayRender("Fri", friday.format("D"), friday.format("MMM")),
          dataIndex: "fri",
          key: "fri",
          width: 65,
        },
      ],
    },
    {
      title: (
        <TotalRender
          total={allSaturdayTotal}
          min={MIN_WORKING_HOURS}
          max={MAX_WORKING_HOURS}
          isSatOrSun={true}
        />
      ),
      dataIndex: "saturdayTotal",
      key: "saturdayTotal",
      children: [
        {
          title: DayRender("Sat", saturday.format("D"), saturday.format("MMM")),
          dataIndex: "sat",
          key: "sat",
          width: 65,
        },
      ],
    },
    {
      title: (
        <TotalRender
          total={allSundayTotal}
          min={MIN_WORKING_HOURS}
          max={MAX_WORKING_HOURS}
          isSatOrSun={true}
        />
      ),
      dataIndex: "sundayTotal",
      key: "sundayTotal",
      children: [
        {
          title: DayRender("Sun", sunday.format("D"), sunday.format("MMM")),
          dataIndex: "sun",
          key: "sun",
          width: 65,
        },
      ],
    },
    {
      title: (
        <TotalRender
          total={allTotal}
          min={MIN_WORKING_HOURS * 5}
          max={MAX_WORKING_HOURS * 5}
        />
      ),
      dataIndex: "allTotal",
      key: "allTotal",
      children: [
        {
          title: "Sum",
          dataIndex: "sum",
          key: "sum",
          width: 70,
        },
      ],
    },
    // {
    //   title: "Claims",
    //   dataIndex: "claims",
    //   key: "claims",
    //   width: 70,
    // }
  );

  if (!approval) {
    columns.push(actionColumn);
  }
  const allRowsApproved = data.every(
    (item) => (item.client === "ap" ? "approved" : item.status) === "approved"
  );

  const dataSource = data.map((item, index) => {
    const isDeclinedRowBeingEdited =
      item.status === "declined" && editingIndex === index;
    const isEditable =
      (editables && isDeclinedRowBeingEdited) ||
      (!editables &&
        (item.status === "withdrawn" ||
          item.status === "draft" ||
          (editable &&
            item.status !== "approved" &&
            item.status !== "declined" &&
            item.status !== "pending" &&
            (item.status === "pending" && !item?.isNew)
          ))) ||
      item.status === "draft" ||
      item.status === "withdrawn";
    const isEditableForAction =
      (editables && isDeclinedRowBeingEdited) ||
      (!editables &&
        (item.status === "withdrawn" ||
          item.status === "draft" ||
          (editable &&
            item.status !== "approved" &&
            item.status !== "declined" &&
            item.status !== "pending" &&
            item?.client !== null))) ||
      item.status === "draft" ||
      item.status === "withdrawn" ||
      (item?.client === null && item.status === "pending");

    const actualStatus = item.client === "ap" ? "approved" : item.status;

    const clientLabel =
      item.client === "nc"
        ? "Non Chargeable"
        : item.client === "ap"
          ? "Absence"
          : clientOptions?.find((c) => c.value === item.client)?.label || "-";

    const matterLabel =
      item.client === "nc"
        ? clientOptions?.find((c) => c.value === item.ncClient)?.label || ""
        : item.client === "ap"
          ? "Approved Absence"
          : engagements?.find((e) => e.matterId === item.engagement)?.name ||
          "-";

    return {
      key: index,
      isEditable,
      rowStatus: actualStatus,
      personInCharge: item.personInCharge,
      clientLabel,
      matterLabel,
      taskLabel: item.task || "-",
      remarksText: item.remarks || "",
      declinedReasonText:
        item.status === "declined"
          ? item.declined?.reason || "No reason provided"
          : "",
      type: <TypeRender index={index} client={item.client} />,
      client: (
        <ClientRender
          editable={isEditable}
          name={[index, "client"]}
          clientOptions={[
            {
              label: "NC",
              value: "nc",
              isLoaded: true,
            },
            {
              label: "AP",
              value: "ap",
              isLoaded: true,
            },
            ...clientOptions,
          ]}
          extraActions={extraActions}
          containerRef={containerRef}
          status={actualStatus}
          week={week}
        />
      ),
      engagement: (
        <EngagementRender
          editable={isEditable}
          name={[index, "engagement"]}
          engagements={engagements}
          containerRef={containerRef}
          week={week}
        />
      ),
      ncClient: (
        <NCClientRender
          editable={isEditable}
          name={[index, "ncClient"]}
          clientOptions={clientOptions}
          containerRef={containerRef}
        />
      ),
      budget: <BudgetRender name={[index, "budget"]} data={data} />,
      actual_to_date: (
        <ActualToDateRender
          timesheetgetdata={timesheetgetdata}
          selectedTabId={selectedTabId}
          name={[index, "actual_to_date"]}
          extraActions={extraActions}
          engagements={engagements}
          data={data}
          week={week}
        />
      ),
      mon: (
        <ChargeAbleRender
          editable={isEditable}
          name={[index, "mon"]}
          date={monday}
          holidays={holidays}
          leaves={leaves}
          status={item.status}
          timesheetgetdata={timesheetgetdata}
        />
      ),
      tue: (
        <ChargeAbleRender
          editable={isEditable}
          name={[index, "tue"]}
          date={tuesday}
          holidays={holidays}
          leaves={leaves}
          status={item.status}
          timesheetgetdata={timesheetgetdata}
        />
      ),
      wed: (
        <ChargeAbleRender
          editable={isEditable}
          name={[index, "wed"]}
          date={wednesday}
          holidays={holidays}
          leaves={leaves}
          status={item.status}
          timesheetgetdata={timesheetgetdata}
        />
      ),
      thu: (
        <ChargeAbleRender
          editable={isEditable}
          name={[index, "thu"]}
          date={thursday}
          holidays={holidays}
          leaves={leaves}
          status={item.status}
          timesheetgetdata={timesheetgetdata}
        />
      ),
      fri: (
        <ChargeAbleRender
          editable={isEditable}
          name={[index, "fri"]}
          date={friday}
          holidays={holidays}
          leaves={leaves}
          status={item.status}
          timesheetgetdata={timesheetgetdata}
        />
      ),
      sat: (
        <ChargeAbleRender
          editable={isEditable}
          name={[index, "sat"]}
          date={saturday}
          holidays={holidays}
          leaves={leaves}
          status={item.status}
          timesheetgetdata={timesheetgetdata}
        />
      ),
      sun: (
        <ChargeAbleRender
          editable={isEditable}
          name={[index, "sun"]}
          date={sunday}
          holidays={holidays}
          leaves={leaves}
          status={item.status}
          timesheetgetdata={timesheetgetdata}
        />
      ),
      sum: (
        <SumRender
          name={[index, "sum"]}
          status={actualStatus}
          allRowsApproved={allRowsApproved}
          editable={editable}
          client={item.client}
          isEditable={isEditable}
        />
      ),
      claims: (
        <ClaimsRender
          editable={isEditable}
          index={index}
          claims={item?.claims}
          extraActions={extraActions}
          values={values}
          engagements={engagements}
        />
      ),
      action: (
        <ActionRender
          editable={isEditableForAction}
          status={actualStatus}
          personInCharge={item.personInCharge}
          index={index}
          approval={approval}
          extraActions={extraActions}
          values={values}
        />
      ),
      extraSection: (
        <ExtraSectionRender
          index={index}
          clientOptions={clientOptions}
          declined={item.status === "declined" ? item.declined : false}
          editable={isEditable}
          containerRef={containerRef}
          holidays={holidays}
          leaves={leaves}
          resources={resources}
          data={data}
          timesheetgetdata={timesheetgetdata}
        />
      ),
    };
  });

  const eligibleTimesheetKeys = dataSource
    .filter(
      (row) => row.rowStatus === "pending" && row.personInCharge === userInfo?.userid
    )
    .map((row) => row.key);

  return {
    dataSource,
    columns,
    selectedTimesheetKeys,
    setSelectedTimesheetKeys,
    eligibleTimesheetKeys,
    approval,
    extraActions,
    week,
    weekDays: [
      { key: "mon", date: monday, total: allMondayTotal },
      { key: "tue", date: tuesday, total: allTuesdayTotal },
      { key: "wed", date: wednesday, total: allWednesdayTotal },
      { key: "thu", date: thursday, total: allThursdayTotal },
      { key: "fri", date: friday, total: allFridayTotal },
      { key: "sat", date: saturday, total: allSaturdayTotal },
      { key: "sun", date: sunday, total: allSundayTotal },
    ],
    allTotal,
  };
};

/* Single source of truth for the hours-column geometry shared by
   WeekTotalsBar (header), the editable EntryCard row, and the locked/view
   EntryCard row, so the three can never drift out of alignment again (see
   the regression this previously caused, once fixed by hand in each spot
   independently). Consumed via var(--ts-*) everywhere below instead of
   copy-pasted literals. */
const timesheetGridVars = css`
  --ts-hours-strip: 644px;
  --ts-day-cell: 70px;
  --ts-gap: 12px;
  --ts-locked-fields: 384px;

  @media (max-width: 991px) {
    --ts-hours-strip: 362px;
    --ts-day-cell: 40px;
    --ts-gap: 6px;
    --ts-locked-fields: 256px;
  }

  @media (max-width: 768px) {
    --ts-hours-strip: 307px;
    --ts-day-cell: 34px;
    --ts-gap: 5px;
    --ts-locked-fields: 212px;
  }

  @media (max-width: 576px) {
    --ts-hours-strip: 252px;
    --ts-day-cell: 28px;
    --ts-gap: 4px;
    --ts-locked-fields: 168px;
  }
`;

const WeekTotalsBar = styled.div`
  ${timesheetGridVars}
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  align-items:center;
  ${'' /* flex-wrap: wrap; */}
  position: sticky;
  top: var(--timesheet-breadcrumb-h, 17px);
  z-index: 20;
  background: #ffffff;
  padding: 12px 40px;
  margin: 0 -24px 24px;
  border-bottom: 1px solid #e2e2e8;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08);
  gap: 24px;
  will-change: transform;
  transform: translateZ(0);

  .week-nav-cell {
    flex: 1 1 180px;
    min-width: 170px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 4px;
    padding: 0;

    .wn-date-nav {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .approve-select-all-checkbox.ant-checkbox-wrapper-checked .ant-checkbox-inner {
      background-color: #12b76a !important;
      border-color: #12b76a !important;
    }

    .wn-left-icon,
    .wn-right-icon {
      width: 24px;
      height: 24px;
      cursor: pointer;
      opacity: 0.6;

      &:hover {
        opacity: 1;
      }
    }

    .wn-current-week {
      cursor: pointer;
      font-weight: 600;
      font-size: 15px;
      color: #1b1b1b;
      background: transparent;
      padding: 0;
      border-radius: 0;
      white-space: nowrap;
    }
  }

  .week-nav-spacer {
    flex: 1 1 180px;
  }

  .days-row {
    flex: 0 1 var(--ts-hours-strip);
    min-width: 0;
    display: flex;
    align-items: flex-end;
    gap: var(--ts-gap);
  }

  .day-cell {
    flex: 0 1 var(--ts-day-cell);
    min-width: 0;
    text-align: center;
    padding: 0;

    &.total-cell {
      flex: 0 1 var(--ts-day-cell);
      min-width: 0;
    }

    .day-date-num {
      font-family: "DM Sans", sans-serif;
      font-weight: 700;
      font-size: 18px;
      line-height: 28px;
      color: #191c1d;
    }

    .day-name {
      font-family: "DM Sans", sans-serif;
      font-weight: 600;
      font-size: 12px;
      line-height: 16px;
      letter-spacing: 0.6px;
      text-transform: uppercase;
      color: #555f6f;
    }
    .day-hour {
      font-family: "DM Sans", sans-serif;
      font-weight: 600;
      font-size: 10px;
      line-height: 16px;
      letter-spacing: 0.6px;
      text-transform: uppercase;
      color: #555f6f;
    }

    .total {
      font-family: "DM Sans", sans-serif;
      font-weight: 700;
      font-size: 18px;
      line-height: 28px;
    }
  }

  @media (max-width: 991px) {
    padding: 0 12px;
    gap: 16px;

    .week-nav-cell,
    .week-nav-spacer {
      flex: 1 1 120px;
    }

    .day-date-num,
    .total {
      font-size: 15px;
      line-height: 22px;
    }

    .day-name {
      font-size: 10px;
    }
  }

  @media (max-width: 768px) {
    padding: 0 12px;
    gap: 12px;

    .week-nav-cell,
    .week-nav-spacer {
      flex: 1 1 100px;
    }

    .day-date-num,
    .total {
      font-size: 13px;
      line-height: 20px;
    }

    .day-name {
      font-size: 9px;
    }
  }

  @media (max-width: 576px) {
    gap: 8px;

    .week-nav-cell,
    .week-nav-spacer {
      flex: 1 1 80px;
    }

    .day-date-num,
    .total {
      font-size: 12px;
      line-height: 16px;
    }

    .day-name {
      font-size: 8px;
    }
  }
`;

const EntryCardsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const EntryCard = styled.div`
  ${timesheetGridVars}
  background: #f3f4f5;
  border-radius: 16px;
  padding: 16px;

  .card-row {
    display: flex;
    align-items: flex-start;
    gap: 24px;
    ${'' /* flex-wrap: wrap; */}
  }

  .card-row-2 {
    margin-top: 24px;
    padding-top: 0;
    border-top: none;
    align-items: flex-end;
  }

  .field {
    flex: 1 1 180px;
    min-width: 0;

    label {
      display: block;
      font-family: "DM Sans", sans-serif;
      font-size: 12px;
      font-weight: 600;
      color: #636980;
      letter-spacing: 0.6px;
      margin-bottom: 8px;
    }

    /* A long selected value (e.g. a long client/matter name) must not
       widen the select box past its column width - shrink and ellipsize
       instead. */
    .ant-select-selector {
      min-width: 0;
    }

    .ant-select-selection-item {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
    }
  }

  .remarks-field {
    position: relative;

    .ant-input-textarea-show-count::after {
      position: absolute;
      top: -22px;
      right: 0;
      float: none;
      font-size: 12px;
      color: #9aa1ae;
    }
  }

  .field-spacer {
    flex: 0 1 var(--ts-hours-strip);
    min-width: 0;
  }

  .ant-select-selector,
  .ant-picker {
    border-radius: 8px !important;
    border-color: #d9d9d9 !important;
    min-height: 38px;
    align-items: center;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder,
  .ant-picker-input input {
    font-family: "DM Sans", sans-serif;
    font-size: 14px;
    color: #1f243b;
  }

  /* Widths/gaps come from the shared --ts-* variables (see
     timesheetGridVars above) so this strip can never drift out of
     alignment with WeekTotalsBar's header or the locked/view row again. */
  .hours-strip {
    flex: 0 1 var(--ts-hours-strip);
    min-width: 0;
    display: flex;
    align-items: flex-end;
    gap: var(--ts-gap);
  }

  .hour-box {
    flex: 0 1 var(--ts-day-cell);
    min-width: 0;

    .ant-input {
      width: 100%;
      height: 62px;
      border-radius: 8px;
      border-color: #d9d9d9;
      text-align: center;
      font-family: "DM Sans", sans-serif;
      font-weight: 700;
      font-size: 16px;
      color: #1f243b;
    }
  }

  .total-box {
    flex: 0 1 var(--ts-day-cell);
    min-width: 0;
    height: 62px;

    .sum-hidden-field {
      display: none;
    }

    .sum-box {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      border-radius: 12px;
      background: #eceef1;

      .sum-value {
        font-family: "DM Sans", sans-serif;
        font-weight: 700;
        font-size: 20px;
        line-height: 1;
        color: #636980;
      }

      .sum-label {
        font-family: "DM Sans", sans-serif;
        font-weight: 600;
        font-size: 11px;
        color: #636980;
      }

      &.blue {
        background: #d5e3fc;

        .sum-value,
        .sum-label {
          color: #1e40af;
        }
      }

      &.green {
        background: #e3f6e8;

        .sum-value,
        .sum-label {
          color: #1a7f37;
        }
      }

      &.absence-total {
        background: #ffdad6;

        .sum-value,
        .sum-label {
          color: #93000a;
        }
      }
    }
  }

  .row-actions {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;
  }

  .status-icon {
    width: 30px;
    height: 30px;
    object-fit: contain;
    cursor: default;

    &.clickable {
      cursor: pointer;

      &:hover {
        opacity: 0.7;
      }
    }
  }

  .type-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 6px;
    white-space: nowrap;

    &.chargeable {
      background: #d2ece7;
      color: #000000;
    }

    &.non-chargeable {
      background: #d5d7dd;
      color: #000000;
    }

    .type-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${COLORS.PRIMARY};
    }
  }

  .row-footer {
    flex: 0 1 var(--ts-hours-strip);
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 20px;
  }

  .row-footer-actions {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  /* Actual to Budget group: BudgetRender and ActualToDateRender now render
     the value as a plain <span class="atb-text"> for display, alongside a
     visually-hidden Form.Item (.atb-hidden-field) that keeps the value in
     the antd form model for calculations/submission. A <span> has no
     wrapper markup and sizes exactly to its text - this is what actually
     fixes the layout gap; a disabled antd <Input>, no matter how its own
     width/padding is styled, always carries Form.Item wrapper divs that
     claim block-level flex space independent of the input's content. */
  .actual-to-budget {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    font-family: "DM Sans", sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #636980;

    .atb-hidden-field {
      display: none;
    }

    .atb-text {
      font-weight: 600;
      color: #636980;
    }
  }

  &.locked {
    display: flex;
    align-items: stretch;
    background: #ffffff;
    border: 1px solid #e2e2e8;
    padding: 0;
    overflow: hidden;
  }

  .locked-checkbox {
    flex: 0 0 72px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    border-right: 1px solid #e2e2e8;

    .approve-checkbox-icon {
      font-size: 34px;
      border-radius: 8px;

      &.approved {
        color: #12b76a;
        opacity: 0.6;
      }

      &.declined {
        color: #ee2738;
        opacity: 0.6;
      }
    }

    .approve-checkbox .ant-checkbox-inner {
      width: 24px;
      height: 24px;
      border-radius: 6px;
    }

    .approve-checkbox.ant-checkbox-wrapper-checked .ant-checkbox-inner {
      background-color: #12b76a !important;
      border-color: #12b76a !important;
    }
  }

  .locked-body {
    flex: 1 1 auto;
    min-width: 0;
  }

  .locked-header {
    display: flex;
    align-items: center;
    gap: 14px;
    background: #f4f5f7;
    padding: 14px 18px;

    .entry-client-name {
      font-family: "DM Sans", sans-serif;
      font-weight: 700;
      color: #1f243b;
      font-size: 14px;
    }

    .entry-matter-name {
      font-family: "DM Sans", sans-serif;
      color: #1f243b;
      font-size: 14px;
      padding-left: 14px;
      border-left: 1px solid #d9d9d9;
    }

    .type-tag {
      margin-left: -2px;

      &.non-chargeable {
        margin-left: 6px;
      }
    }

    .header-actions {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-left: auto;

      .status {
        font-family: "DM Sans", sans-serif;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        padding: 4px 12px;
        border-radius: 12px;

        &.approved {
          color: #12b76a;
          background: transparent;
        }

        &.pending {
          background: #ffdda3;
        }

        &.declined {
          background: #ffd9dd;
        }

        &.withdrawn {
          background: #fde3e3;
        }
      }
    }
  }

  .locked-content {
    display: flex;
    align-items: center;
    ${'' /* flex-wrap: wrap; */}
    gap: 24px;
    padding: 18px 16px;
  }

  /* Basis comes from --ts-locked-fields (shared with the header's/editable
     card's combined field-slot width) so .hours-strip below lands at the
     same position as it does in the header and editable cards. */
  .locked-fields {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1 1 var(--ts-locked-fields);
    min-width: 0;
  }

  .locked-field {
    display: flex;
    gap: 16px;
    font-family: "DM Sans", sans-serif;
    font-size: 13px;

    label {
      width: 90px;
      flex-shrink: 0;
      color: #636980;
    }

    span {
      color: #1f243b;
      flex: 1 1 auto;
      min-width: 0;
      max-width: 100%;
      overflow-wrap: anywhere;
    }
  }

  .hour-box.plain {
    text-align: center;
    font-family: "DM Sans", sans-serif;
    font-weight: 700;
    font-size: 15px;
    color: #1f243b;

    .ant-input[disabled] {
      border: none;
      background: transparent;
      color: #1f243b;
      font-weight: 700;
    }
  }

  .total-box.locked-total {
    height: 62px;
  }

  @media (max-width: 991px) {
    padding: 12px;

    .card-row {
      gap: 16px;
    }

    .field {
      flex: 1 1 120px;
    }

    .field-spacer {
      flex: 0 0 var(--ts-hours-strip);
      width: var(--ts-hours-strip);
    }

    .locked-content {
      gap: 16px;
      padding: 18px 11px;
    }

    .hour-box,
    .total-box {
      height: 44px;

      .ant-input {
        height: 44px;
        font-size: 13px;
      }

      .sum-box {
        .sum-value {
          font-size: 15px;
        }

        .sum-label {
          font-size: 9px;
        }
      }
    }
  }

  @media (max-width: 768px) {
    .card-row {
      gap: 12px;
    }

    .field {
      flex: 1 1 100px;
    }

    .field-spacer {
      flex: 0 0 var(--ts-hours-strip);
      width: var(--ts-hours-strip);
    }

    .locked-content {
      gap: 12px;
    }

    .hour-box,
    .total-box {
      height: 38px;

      .ant-input {
        height: 38px;
        font-size: 12px;
      }

      .sum-box {
        .sum-value {
          font-size: 13px;
        }

        .sum-label {
          font-size: 8px;
        }
      }
    }
  }

  @media (max-width: 576px) {
    .card-row {
      gap: 8px;
    }

    .field {
      flex: 1 1 80px;
    }

    .field-spacer {
      flex: 0 0 var(--ts-hours-strip);
      width: var(--ts-hours-strip);
    }

    .locked-content {
      gap: 8px;
    }

    .hour-box,
    .total-box {
      height: 34px;

      .ant-input {
        height: 34px;
        font-size: 11px;
      }

      .sum-box {
        .sum-value {
          font-size: 12px;
        }

        .sum-label {
          font-size: 7px;
        }
      }
    }
  }
`;

const ClaimsTable = styled(Table)`
  th.ant-table-cell {
    background-color: #f5f5f5;
  }
  .ant-table-cell {
    padding: 6px 10px !important;
    font-size: 12px;
  }
  .ant-table-thead > tr > th {
    overflow-wrap: normal;
  }
  .ant-table-cell.nowrap {
    white-space: nowrap;
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-close-x {
    .anticon-close.ant-modal-close-icon,
    svg {
      width: 20px;
      height: 20px;
    }
  }
  .ant-modal-header {
    border-bottom: none;
    padding-top: 10px;
  }
  .ant-modal-title {
    font-weight: 500;
    text-align: left;
    color: #1b1b1b;
  }
  .ant-modal-content {
    border-radius: 10px;
  }
  .anticon {
    color: #000000;
    width: 14px;
    height: 14px;
  }
  .ant-modal-body {
    padding-top: 0px;
  }
  .ant-modal-footer {
    display: none;
  }
  .ant-btn-text:focus,
  .ant-btn-text:hover {
    background: #26a4ff;
    color: #ffffff;
  }
  .ant-checkbox.ant-checkbox-checked {
    .ant-checkbox-inner {
      background-color: #02b209 !important;
      border-color: #02b209 !important;
    }
  }
  .ant-upload-span {
    cursor: pointer;
  }
`;

const ResubmitModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
  }

  .ant-modal-header {
    border-radius: 16px 16px 0 0;
    border-bottom: none;
    padding: 20px 24px 0;
  }

  .ant-modal-title {
    font-family: "DM Sans", sans-serif;
    font-weight: 600;
    font-size: 20px;
    color: #1f243b;
  }

  .ant-modal-close {
    top: 16px;
    right: 16px;
  }

  .ant-modal-body {
    padding: 20px 24px 24px;
  }

  .resubmit-card {
    background: #f5f6f8;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 24px;
  }

  .resubmit-fields-row {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;

    &:not(:last-child) {
      margin-bottom: 16px;
    }

    .field,
    .field-spacer {
      flex: 1 1 180px;
      min-width: 0;
    }

    .field {
      font-family: "DM Sans", sans-serif;
      font-size: 15px;
      color: #1f243b;

      label,
      .field-label {
        display: block;
        font-family: "DM Sans", sans-serif;
        font-size: 13px;
        font-weight: 500;
        color: #6b7280;
        margin-bottom: 6px;
      }
    }

    .remarks-field {
      position: relative;

      .ant-input-textarea-show-count::after {
        position: absolute;
        top: -22px;
        right: 0;
        float: none;
        font-size: 12px;
        color: #9aa1ae;
      }
    }

    .declined-reason {
      display: none;
    }
  }

  .resubmit-days-row {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    margin-bottom: 16px;
  }

  .resubmit-day-cell {
    flex: 1 1 0;
    min-width: 0;
    text-align: center;

    &.total-cell {
      flex: 1 1 0;
    }

    .resubmit-day-date {
      font-family: "DM Sans", sans-serif;
      font-weight: 700;
      font-size: 18px;
      line-height: 28px;
      color: #191c1d;
    }

    .resubmit-day-name {
      font-family: "DM Sans", sans-serif;
      font-weight: 600;
      font-size: 12px;
      line-height: 16px;
      letter-spacing: 0.6px;
      text-transform: uppercase;
      color: #555f6f;
    }

    .total {
      font-family: "DM Sans", sans-serif;
      font-weight: 700;
      font-size: 18px;
      line-height: 28px;
      color: #000000;

      &.error {
        color: #ba1a1a;
      }
      &.good {
        color: #2d932d;
      }
    }
  }

  .resubmit-hours-row {
    display: flex;
    gap: 12px;
    margin: 0;

    .hour-box {
      flex: 1 1 0;
      min-width: 0;

      .ant-input {
        width: 100%;
        height: 54px;
        border-radius: 8px;
        border-color: #d9d9d9;
        text-align: center;
        font-family: "DM Sans", sans-serif;
        font-weight: 700;
        font-size: 16px;
        color: #1f243b;
      }
    }

    .total-box {
      flex: 1 1 0;
      min-width: 0;
      height: 54px;

      .sum-hidden-field {
        display: none;
      }

      .sum-box {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        border-radius: 8px;
        background: #eceef1;

        .sum-value {
          font-family: "DM Sans", sans-serif;
          font-weight: 700;
          font-size: 20px;
          line-height: 1;
          color: #636980;
        }

        .sum-label {
          font-family: "DM Sans", sans-serif;
          font-weight: 600;
          font-size: 11px;
          color: #636980;
        }

        &.blue {
          background: #d5e3fc;

          .sum-value,
          .sum-label {
            color: #1e40af;
          }
        }

        &.green {
          background: #e3f6e8;

          .sum-value,
          .sum-label {
            color: #1a7f37;
          }
        }

        &.absence-total {
          background: #ffdad6;

          .sum-value,
          .sum-label {
            color: #93000a;
          }
        }
      }
    }
  }

  .resubmit-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 20px;
    border-top: 1px solid #eceef1;

    .ant-btn {
      height: 40px;
      padding: 0 28px;
      border-radius: 8px;
      font-family: "DM Sans", sans-serif;
      font-weight: 500;
      font-size: 14px;
    }

    .resubmit-submit {
      background: ${COLORS.PRIMARY};
      border-color: ${COLORS.PRIMARY};
    }
  }
`;

const TimeSheetWrap = styled.div`
  table {
    box-shadow: 0px 3px 6px #00000015;
  }

  .ant-form-item {
    margin-bottom: 0px;
  }

  .ant-input {
    border-radius: 5px;
    border-color: #d9d9d9;
    padding-left: 0px;
    padding-right: 0px;
    text-align: center;
  }

  .ant-input.start {
    text-align: start;
    padding-left: 10px;
    padding-right: 10px;
  }

  .ant-form-item-label {
    padding-bottom: 4px !important;
    label {
      padding-bottom: 0px !important;
      font-size: 12px;
      color: #000000;
    }
  }

  .upload-button {
    border-radius: 5px;
    width: 100%;
  }

  .add-claims {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    padding: 0 16px;
    border: 1px solid #e2e2e8;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: none;
    font-family: "DM Sans", sans-serif;
    font-size: 16px;
    font-weight: 400;
    cursor: pointer;
    color: #0066ff !important;

    &:hover,
    &:focus {
      background: #ffffff;
      border-color: #0066ff;
      box-shadow: 0px 3px 6px #00000015;
      color: #0066ff !important;
    }

    &:disabled,
    &.ant-btn-disabled {
      background: #ffffff;
      border-color: #e2e2e8;
      color: #a7b0c0 !important;
      cursor: not-allowed;
    }
  }

  .client-name {
    font-size: 12px;
    padding: 0.5rem 1rem;
    margin-right: auto;
    font-weight: 600;
    color: #397ef4;
    cursor: pointer;
  }

  .action {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;

    .icon-btn {
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border: 1px solid #e2e2e8;
      border-radius: 8px;
      background: #ffffff;
      color: #ee2738;
      font-size: 16px;

      &:hover {
        border-color: #ee2738;
        box-shadow: 0px 3px 6px #00000015;
      }
    }
  }

  .declined-reason {
    width: 100%;
    background-color: #ffe8e8;
    color: #ff4d4f;
    font-weight: 700px;
    margin-top: 0.5rem;
    padding: 0.5rem 1rem;
  }

  .ant-form-item-explain-error {
    font-size: 10px;
  }

  .total {
    font-size: 12px;
    font-weight: 600;
    color: #000000;
    &.error {
      color: #ba1a1a;
    }
    &.good {
      color: #2d932d;
    }
  }

  .timeSheetTable {
    margin-bottom: 40px;
  }
`;